<template>
  <section class="style-replace-workspace">
    <div class="replace-flow">
      <section class="flow-panel upload-panel">
        <header class="flow-title"><span>01</span><div><strong>上传原有效果图</strong><small>这张图将作为空间结构和镜头的唯一依据</small></div></header>
        <label
          v-if="!lightingAdjustment || !previewUrl"
          class="effect-dropzone"
          :class="{ filled: previewUrl, dragging, invalid: fileValidationError }"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="handleDrop"
        >
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            :aria-invalid="Boolean(fileValidationError)"
            :aria-describedby="fileValidationError ? 'source-file-error' : (!previewUrl ? 'source-file-help' : null)"
            @change="handleFileInput"
          />
          <img v-if="previewUrl" :src="$ossImageUrl(previewUrl, 'preview')" :alt="lightingMode ? '待转换灯光的原始效果图' : '待替换风格的原始效果图'" decoding="async" />
          <span v-else class="dropzone-copy">
            <i class="el-icon-upload2"></i>
            <strong>点击或拖入原有效果图</strong>
            <small id="source-file-help">PNG / JPG / WebP，单张不超过 10MB</small>
            <span class="dropzone-guides" aria-label="原图上传建议">
              <em><i class="el-icon-full-screen" aria-hidden="true"></i> 标准 16:9 · 2K 2048×1152</em>
              <em><i class="el-icon-picture-outline" aria-hidden="true"></i> 上传清晰完整的单张原图</em>
            </span>
          </span>
          <span v-if="previewUrl" class="replace-image-action"><i class="el-icon-refresh"></i> 更换原图</span>
        </label>
        <LightingZoneEditor
          v-else
          :image-url="previewUrl"
          :zones="lightingZones"
          :active-zone-id="activeLightingZoneId"
          :drawing-enabled.sync="zoneDrawingEnabled"
          @create-zone="createLightingZone"
          @select-zone="selectLightingZone"
          @clear="clearLightingZones"
          @replace-image="openLightingFilePicker"
        />
        <input
          v-if="lightingAdjustment"
          ref="lightingFileInput"
          class="visually-hidden-file"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          :aria-invalid="Boolean(fileValidationError)"
          :aria-describedby="fileValidationError ? 'source-file-error' : null"
          @change="handleFileInput"
        />
        <div
          v-if="fileValidationError"
          id="source-file-error"
          class="file-validation-error"
          role="alert"
          aria-live="assertive"
        >
          <i class="el-icon-warning-outline" aria-hidden="true"></i>
          <div>
            <strong>图片无法上传</strong>
            <span>{{ fileValidationError }}</span>
          </div>
        </div>
        <aside
          v-if="sourceRatioWarning"
          id="source-ratio-warning"
          class="ratio-preflight-warning source-ratio-warning"
          role="status"
          aria-live="polite"
          aria-label="原图比例建议"
        >
          <i class="el-icon-warning-outline" aria-hidden="true"></i>
          <div>
            <strong>当前图片不符合建议画幅，生成结果可能不够精准</strong>
            <span>{{ sourceRatioWarning }}</span>
          </div>
          <button type="button" @click="goToSourceCrop">
            调整为 16:9 <i class="el-icon-right" aria-hidden="true"></i>
          </button>
        </aside>
        <p class="lock-rules-caption"><i class="el-icon-circle-check" aria-hidden="true"></i> 灯光转换时，系统将重点保持以下内容不变</p>
        <div class="lock-rules">
          <span><i class="el-icon-lock"></i> 锁定镜头透视</span>
          <span><i class="el-icon-lock"></i> 锁定门窗结构</span>
          <span><i class="el-icon-lock"></i> 锁定家具布局</span>
        </div>
        <aside
          v-if="lightingMode && !lightingAdjustment && !previewUrl"
          class="ratio-preflight-warning"
          role="note"
          aria-label="灯光转换前的图片比例建议"
        >
          <i class="el-icon-info" aria-hidden="true"></i>
          <div>
            <strong>为保障灯光转换效果，建议先统一为标准 16:9</strong>
            <span>建议尺寸：2K 为 2048×1152，4K 为 3840×2160。操作前可前往“原图裁切”，将非标准原图处理为规范画幅。</span>
          </div>
          <button type="button" @click="goToSourceCrop">
            前往原图裁切 <i class="el-icon-right" aria-hidden="true"></i>
          </button>
        </aside>
      </section>

      <section class="flow-panel style-panel">
        <header class="flow-title"><span>02</span><div><strong>{{ lightingAdjustment ? '设置每个区域的灯光状态' : lightingMode ? '选择灯光转换效果' : '选择替换后的风格' }}</strong><small>{{ lightingAdjustment ? '在左侧框选灯光后，分别设置点亮或关闭' : lightingMode ? '可单选、多选或全选；高位、低位照明同样按夜景生成' : '只能选择一种目标风格' }}</small></div></header>
        <div v-if="lightingAdjustment" class="zone-lighting-control">
          <div class="zone-control-summary">
            <span class="zone-summary-icon"><i class="el-icon-set-up"></i></span>
            <div>
              <strong>灯光区域控制</strong>
              <small v-if="lightingZones.length">已设置 {{ enabledLightingZoneCount }} 处点亮、{{ disabledLightingZoneCount }} 处关闭</small>
              <small v-else>请先在左侧效果图中框选需要控制的灯光</small>
            </div>
            <span class="zone-count">{{ lightingZones.length }}</span>
          </div>

          <div v-if="lightingZones.length" class="zone-list">
            <article
              v-for="(zone, index) in lightingZones"
              :key="zone.id"
              :ref="`lightingZoneControl-${zone.id}`"
              :class="{ active: zone.id === activeLightingZoneId }"
              :aria-current="zone.id === activeLightingZoneId ? 'true' : null"
              @click="selectLightingZone(zone.id)"
            >
              <span class="zone-number">{{ index + 1 }}</span>
              <div class="zone-name">
                <strong>灯光区域 {{ index + 1 }}</strong>
                <small>画面位置 {{ zonePositionText(zone) }}</small>
              </div>
              <div class="zone-state-switch" role="group" :aria-label="`灯光区域 ${index + 1} 状态`">
                <button
                  type="button"
                  :class="{ active: zone.state === 'on' }"
                  :disabled="generating"
                  @click.stop="setLightingZoneState(zone.id, 'on')"
                >
                  <i class="el-icon-sunny"></i> 点亮
                </button>
                <button
                  type="button"
                  :class="{ active: zone.state === 'off' }"
                  :disabled="generating"
                  @click.stop="setLightingZoneState(zone.id, 'off')"
                >
                  <i class="el-icon-moon"></i> 关闭
                </button>
              </div>
              <button
                type="button"
                class="zone-delete"
                title="删除这个区域"
                :disabled="generating"
                @click.stop="removeLightingZone(zone.id)"
              >
                <i class="el-icon-delete"></i>
              </button>
            </article>
          </div>

          <button v-else type="button" class="zone-empty-action" :disabled="!previewUrl" @click="startZoneDrawing">
            <i class="el-icon-crop"></i>
            <span><strong>{{ previewUrl ? '开始框选灯光区域' : '上传效果图后开始框选' }}</strong><small>可以连续框选多个区域，并分别设置亮或不亮</small></span>
          </button>

          <div v-if="lightingZones.length" class="on-brightness-control">
            <div>
              <span>点亮区域亮度</span>
              <strong>{{ lightingBrightness }}%</strong>
            </div>
            <input
              v-model.number="lightingBrightness"
              type="range"
              min="20"
              max="100"
              step="5"
              :disabled="generating || !enabledLightingZoneCount"
              @input="resetLightingAdjustmentResult"
            />
            <div class="brightness-scale"><span>柔和 20%</span><span>标准 60%</span><span>明亮 100%</span></div>
          </div>

          <label class="zone-prompt-input">
            <span class="zone-prompt-title">
              <strong><i class="el-icon-edit-outline"></i> 补充提示词</strong>
              <span class="zone-prompt-actions">
                <button
                  type="button"
                  :disabled="writingLightingPrompt || lightingPromptCooldown || generating"
                  :aria-busy="String(writingLightingPrompt)"
                  @click.prevent="writeLightingPrompt"
                >
                  <i :class="writingLightingPrompt ? 'el-icon-loading' : lightingPromptCooldown ? 'el-icon-circle-check' : 'el-icon-magic-stick'"></i>
                  {{ writingLightingPrompt ? 'AI 正在写' : lightingPromptCooldown ? '请稍候' : 'AI 帮写' }}
                </button>
                <em>选填 · {{ requirement.length }}/300</em>
              </span>
            </span>
            <textarea
              v-model="requirement"
              maxlength="300"
              :disabled="generating"
              placeholder="例如：灯光保持 3000K 暖白色，光线柔和自然；吊顶灯带亮度均匀，避免过曝，墙面保留细腻的明暗层次"
              @input="resetLightingAdjustmentResult"
            ></textarea>
            <AiPromptOptimizer
              v-model="requirement"
              :disabled="writingLightingPrompt || lightingPromptCooldown || generating"
              :max-length="300"
              context="室内图片局部灯光点亮、关闭和亮度调整的补充要求"
              @input="resetLightingAdjustmentResult"
            />
            <small>会与框选区域的点亮 / 关闭状态一起提交，适合补充色温、氛围、层次和局部亮度要求。</small>
          </label>

          <p class="zone-control-note"><i class="el-icon-info"></i><span>选框只用来识别灯具，生成时会自动扩展为柔边受光区，不会按矩形生硬边。其他区域会用原图像素锁定。</span></p>
        </div>
        <div v-else-if="lightingMode" class="lighting-spec-grid">
          <div class="lighting-multi-toolbar">
            <span><i class="el-icon-circle-check"></i><strong>已选 {{ selectedLightingEffectItems.length }} 项</strong><small>可单选、多选或全部生成</small></span>
            <button type="button" :disabled="generating" @click="toggleAllLightingEffects">
              {{ allLightingEffectsSelected ? '取消全选' : '全部勾选' }}
            </button>
          </div>
          <button
            v-for="item in lightingEffects"
            :key="item.id"
            type="button"
            :class="{ active: selectedLightingEffects.includes(item.id) }"
            :aria-pressed="String(selectedLightingEffects.includes(item.id))"
            :disabled="generating"
            @click="selectLightingEffect(item.id)"
          >
            <span class="lighting-icon" :class="item.tone"><i :class="item.icon"></i></span>
            <strong>{{ item.name }}</strong>
            <small>{{ item.caption }}</small>
            <i v-if="selectedLightingEffects.includes(item.id)" class="lighting-check el-icon-check"></i>
          </button>
        </div>
        <div v-else class="style-grid">
          <button
            v-for="(item, styleIndex) in styles"
            :key="item.name"
            type="button"
            :class="{ active: selectedStyle === item.name }"
            @click="selectedStyle = item.name"
          >
            <span class="style-palette" aria-hidden="true">
              <i v-for="color in stylePalettes[styleIndex]" :key="color" :style="{ backgroundColor: color }"></i>
            </span>
            <span class="style-copy"><strong>{{ item.name }}</strong><small>{{ item.caption }}</small></span>
            <i v-if="selectedStyle === item.name" class="style-selected el-icon-check" aria-hidden="true"></i>
          </button>
        </div>
        <div v-if="!lightingMode" class="style-reference-upload">
          <div class="style-reference-heading">
            <span><strong>上传风格参考图</strong><em>选填</em></span>
            <small><i class="el-icon-info"></i> 仅参考配色、材质与氛围，不改变原图户型和布局</small>
          </div>
          <label
            class="style-reference-dropzone"
            :class="{ filled: styleReferencePreviewUrl, dragging: styleReferenceDragging, invalid: styleReferenceError }"
            @dragover.prevent="styleReferenceDragging = true"
            @dragleave.prevent="styleReferenceDragging = false"
            @drop.prevent="handleStyleReferenceDrop"
          >
            <input
              class="style-reference-file-input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              :disabled="generating"
              aria-label="上传风格参考图"
              @change="handleStyleReferenceInput"
            />
            <img v-if="styleReferencePreviewUrl" :src="styleReferencePreviewUrl" alt="风格参考图" decoding="async" />
            <span v-else class="style-reference-empty">
              <i class="el-icon-picture-outline" aria-hidden="true"></i>
              <span><strong>点击上传或将图片拖到这里</strong><small>支持 PNG、JPG、WebP · 最大 10MB</small></span>
              <b>选择图片</b>
            </span>
            <b v-if="styleReferencePreviewUrl" class="style-reference-replace"><i class="el-icon-refresh"></i> 更换参考图</b>
          </label>
          <div v-if="styleReferencePreviewUrl" class="style-reference-meta">
            <span><i class="el-icon-circle-check"></i> 已添加参考图，生成时作为第二张图片提交</span>
            <button type="button" :disabled="generating" @click="removeStyleReference"><i class="el-icon-delete"></i> 移除</button>
          </div>
          <p v-if="styleReferenceError" class="style-reference-error"><i class="el-icon-warning-outline"></i>{{ styleReferenceError }}</p>
        </div>
        <label v-if="!lightingAdjustment" class="custom-requirement">
          <span>补充要求 <em>选填 · {{ requirement.length }}/300</em></span>
          <textarea v-model="requirement" maxlength="300" :placeholder="lightingAdjustment ? '例如：保持暖白色温，避免过曝；右侧灯带比左侧略暗' : lightingMode ? '例如：室内整体亮度稍暗、更有电影感；严格保留原图全部门窗和开口' : '例如：保留原沙发款式，整体改为暖色调；不要主灯'"></textarea>
          <AiPromptOptimizer
            v-model="requirement"
            :disabled="generating"
            :max-length="300"
            :context="lightingMode ? '室内效果图灯光氛围转换的补充要求' : '室内效果图风格替换的补充要求'"
          />
        </label>
      </section>
    </div>

    <section v-if="!lightingAdjustment" class="prompt-preview-panel">
      <header>
        <div class="prompt-preview-title">
          <span>03</span>
          <div><strong>{{ lightingMode && lightingTaskPrompts.length > 1 ? '独立任务实际执行提示词' : 'FastIMG 最终执行提示词' }}</strong><small>{{ lightingMode && !lightingAdjustment ? (lightingPromptPreviewsReady ? '已由后端按正式生成链路组装，以下内容与实际发送给模型的提示词完全一致' : '正在从后端同步正式生成链路中的实际执行提示词') : '会随目标风格和补充要求实时更新，生成时将完整提交以下内容' }}</small></div>
        </div>
        <button v-if="!(lightingMode && lightingTaskPrompts.length > 1)" type="button" title="复制完整提示词" :disabled="lightingMode && !lightingAdjustment && !lightingPromptPreviewsReady" @click="copyPrompt()"><i class="el-icon-copy-document"></i> 复制提示词</button>
      </header>
      <div v-if="lightingMode && lightingTaskPrompts.length > 1" class="lighting-task-prompts">
        <article v-for="task in lightingTaskPrompts" :key="task.id">
          <header><strong>{{ task.index }}. {{ task.name }}</strong><span>{{ task.ready ? '后端已确认 · 单独提交 1 个任务' : '正在同步实际执行内容' }}</span><button type="button" :disabled="!task.ready" @click="copyPrompt(task.prompt)"><i class="el-icon-copy-document"></i> 复制</button></header>
          <pre>{{ task.prompt }}</pre>
        </article>
      </div>
      <pre v-else>{{ displayedFinalPrompt }}</pre>
    </section>

    <section class="generation-bar">
      <div class="generation-intro">
        <span><i class="el-icon-magic-stick"></i></span>
        <div><strong>生成设置</strong><small>确认输出参数后开始{{ featureAction }}</small></div>
      </div>
      <div class="output-settings">
        <div class="setting-group">
          <span class="setting-label">画幅比例</span>
          <div class="auto-ratio-control" :class="{ detected: sourceWidth && sourceHeight }">
            <strong>{{ selectedRatio }}</strong>
            <span>{{ sourceWidth && sourceHeight ? `${sourceWidth} × ${sourceHeight} · 已按原图自动匹配` : '上传原图后自动识别并锁定' }}</span>
          </div>
        </div>
        <div class="setting-group">
          <span class="setting-label">{{ lightingMode && !lightingAdjustment ? '已选任务' : '方案数量' }}</span>
          <strong class="fixed-output-count">{{ lightingMode && !lightingAdjustment ? `${selectedLightingEffectItems.length} 个` : '1 张' }}</strong>
        </div>
        <ResolutionSelector v-model="selectedResolution" compact :disabled="generating" />
        <QualitySelector v-model="selectedQuality" compact :disabled="generating" />
      </div>
      <button class="generate-button" :disabled="generating || ratioDetecting || (lightingMode && !lightingAdjustment && !selectedLightingEffectItems.length)" @click="generateReplacement">
        <span v-if="generating" class="button-spinner"></span><i v-else class="el-icon-magic-stick"></i>
        <span>{{ generating ? generationText : ratioDetecting ? '正在识别原图画幅' : lightingAdjustment ? '生成分区灯光效果' : lightingMode ? `一键生成已选 ${selectedLightingEffectItems.length} 种灯光` : '开始风格替换' }}</span>
        <small v-if="!generating && !ratioDetecting">{{ lightingMode && !lightingAdjustment ? `每种效果创建 1 个独立任务 · 预计消耗 ${selectedLightingEffectItems.length * selectedQualityPoints} 积分` : `生成 1 张 · 预计消耗 ${selectedQualityPoints} 积分` }}</small>
      </button>
      <div v-if="generating" class="progress-track"><i :style="{ width: `${progress}%` }"></i></div>
    </section>

    <section class="result-section" :class="{ 'lighting-result-section': lightingAdjustment }">
      <header class="result-header">
        <div><span class="replace-kicker">BEFORE & AFTER</span><h2>{{ lightingAdjustment ? '分区灯光控制结果' : lightingMode ? (selectedLightingEffectItems.length > 1 ? '多种灯光转换结果' : `${selectedLightingEffectItem.name}转换结果`) : '替换结果' }}</h2><p>{{ lightingAdjustment ? '对照检查每个框选区域是否按要求点亮或关闭，再下载效果图。' : lightingMode ? '每个勾选效果都是独立任务，并分别保存到下方历史记录。' : '对照检查空间结构是否保持一致，再下载满意方案。' }}</p></div>
        <span v-if="results.length" class="result-badge"><i class="el-icon-circle-check"></i> 已完成 {{ results.length }} 张</span>
      </header>

      <div class="comparison-grid" :class="{ empty: !previewUrl && !results.length }">
        <article class="image-card source-card">
          <header><span>{{ beforeLabel }}</span><strong>原始效果图</strong></header>
          <div class="image-stage" :class="{ 'has-image': previewUrl }">
            <img
              v-if="previewUrl"
              class="zoomable-image"
              :src="$ossImageUrl(previewUrl, 'preview')"
              alt="原始效果图"
              title="点击放大原图"
              @click="previewImage(previewUrl, `${featureName} · ${beforeLabel}`, currentViewerItems, 0)"
            />
            <div v-else class="image-empty"><i class="el-icon-picture-outline"></i><strong>等待上传原图</strong><span>上传后会固定显示在这里便于对照</span></div>
          </div>
        </article>
        <article v-for="(image, index) in resultSlots" :key="index" class="image-card result-card">
          <header><span>{{ afterLabel }}</span><strong>{{ resultStyle(index) }}</strong><WorkflowTuningButton v-if="image" variant="button" icon-only :image-url="image" :prompt="displayedFinalPrompt || finalPrompt" source-model="gpt-image-2-official" :module-name="featureName" label="工作流" /><button v-if="image" title="下载图片" @click="downloadImage(image, index)"><i class="el-icon-download"></i></button></header>
          <div class="image-stage" :class="{ 'has-image': image }">
            <img
              v-if="image"
              class="zoomable-image"
              :src="$ossImageUrl(image, 'preview')"
              :alt="`${resultStyle(index)}${afterLabel}结果`"
              title="点击放大效果图"
              @click="previewImage(image, `${featureName} · ${afterLabel} · ${resultStyle(index)}`, currentViewerItems, (previewUrl ? 1 : 0) + index)"
            />
            <div v-else-if="generating" class="image-empty generating-empty"><span class="result-loader"><i></i><i></i><i></i></span><strong>{{ generationText }}</strong><span>{{ lightingMode ? `正在保持原图内容并执行${taskStyle}` : '正在保持结构并重绘材质与软装' }}</span></div>
            <div v-else class="image-empty"><i class="el-icon-magic-stick"></i><strong>等待{{ featureName }}</strong><span>生成结果会在完成后自动出现</span></div>
          </div>
        </article>
      </div>

      <div v-if="generating || (errorDetails && errorDetails.pending)" class="task-live-status" role="status" aria-live="polite">
        <span class="task-live-spinner"><i></i></span>
        <div class="task-live-copy">
          <strong>生成中 <b>{{ Math.max(1, Math.min(99, Math.round(progress || 1))) }}%</b></strong>
          <span>{{ generationText || 'AI 正在处理图片' }}</span>
          <small>任务正在自动处理，完成后结果会直接显示，无需刷新页面</small>
        </div>
        <div class="task-live-progress" :aria-label="`生成进度 ${Math.round(progress || 1)}%`">
          <i :style="{ width: `${Math.max(2, Math.min(99, progress || 1))}%` }"></i>
        </div>
      </div>

      <div v-else-if="errorDetails" class="generation-error">
        <i class="el-icon-warning-outline"></i>
        <div>
          <strong>本次{{ featureAction }}未完成</strong>
          <span>失败原因：{{ errorDetails.message }}</span>
          <small>处理建议：{{ errorDetails.suggestion }}</small>
        </div>
        <button
          v-if="!errorDetails.pending"
          type="button"
          :disabled="generating"
          aria-label="重试本次生成任务"
          @click="generateReplacement"
        >
          <i class="el-icon-refresh-right" aria-hidden="true"></i>
          重试
        </button>
      </div>
    </section>

    <section class="history-section">
      <header>
        <div><span class="replace-kicker">RECENT TASKS</span><h2>最近的{{ featureName }}</h2></div>
        <button type="button" class="history-refresh" :disabled="historyLoading" @click="loadHistory">
          <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i>
          {{ historyLoading ? '同步中' : '刷新记录' }}
        </button>
      </header>
      <div v-if="historyLoading && !history.length" class="history-loading"><i class="el-icon-loading"></i><strong>正在加载创作历史</strong><span>正在获取{{ featureName }}记录，请稍候…</span></div>
      <div v-else-if="historyDisplayRecords.length" class="history-grid">
        <template v-for="record in historySectionRecords">
          <div
            v-if="record.isHistoryDivider"
            :key="record.id"
            class="history-type-divider"
            role="separator"
            aria-label="以下为单次生成记录"
          >
            <i aria-hidden="true"></i>
            <span>
              <b><i class="el-icon-document"></i> 单次生成记录</b>
              <small>以下为单独生成的灯光任务</small>
            </span>
            <i aria-hidden="true"></i>
          </div>
        <article v-else :key="record.id" :class="{ 'history-group-card': record.isGroup }">
          <template v-if="record.isGroup">
            <div class="history-group-header">
              <span><i class="el-icon-collection"></i> 同次生成组</span>
              <strong>{{ record.children.length }} 种灯光效果</strong>
              <small>{{ groupStatusText(record) }}</small>
              <button
                type="button"
                class="history-group-download-all"
                :disabled="!record.children.some(child => child.result) || downloadingGroupIds.includes(record.id)"
                @click="downloadHistoryGroup(record)"
              >
                <i :class="downloadingGroupIds.includes(record.id) ? 'el-icon-loading' : 'el-icon-download'"></i>
                {{ downloadingGroupIds.includes(record.id) ? '正在打包' : `一键下载（${record.children.filter(child => child.result).length} 张）` }}
              </button>
            </div>
            <div class="history-group-images" :style="{ gridTemplateColumns: `repeat(${record.children.length + 1}, minmax(0, 1fr))` }">
              <div class="history-group-source">
                <button
                  v-if="record.source"
                  type="button"
                  class="history-thumb"
                  title="点击放大本组原图"
                  @click="previewImage(record.source, `灯光转换组 · ${beforeLabel}`, historyViewerItems, historyViewerIndex(record.children[0].id, 'source'))"
                >
                  <img :src="$ossImageUrl(record.source, 'thumbnail')" alt="本组原始效果图" loading="lazy" decoding="async" />
                  <span class="history-image-label">共用原图</span>
                  <i class="el-icon-zoom-in"></i>
                </button>
                <div v-else class="history-group-pending"><i class="el-icon-picture-outline"></i><strong>共用原图</strong><span>原图不可用</span></div>
                <div class="history-image-dimension">
                  <i class="el-icon-full-screen"></i>
                  <span>图片尺寸</span>
                  <strong>{{ historySourceImageDimensions(record) }}</strong>
                </div>
              </div>
              <div v-for="child in record.children" :key="child.id" class="history-group-result">
                <button
                  v-if="child.result"
                  type="button"
                  class="history-thumb"
                  :title="`点击放大${child.style}结果`"
                  @click="previewImage(child.result, `${child.style} · ${afterLabel}`, historyViewerItems, historyViewerIndex(child.id, 'result'))"
                >
                  <img :src="$ossImageUrl(child.result, 'thumbnail')" :alt="`${child.style}结果`" loading="lazy" decoding="async" />
                  <span class="history-image-label result-label">{{ child.style }}</span>
                  <i class="el-icon-zoom-in"></i>
                </button>
                <div v-else class="history-group-pending">
                  <i :class="child.status === 'failed' ? 'el-icon-warning-outline' : 'el-icon-loading'"></i>
                  <strong>{{ child.style }}</strong>
                  <span>{{ statusText(child.status) }}</span>
                </div>
                <div class="history-image-dimension">
                  <i class="el-icon-full-screen"></i>
                  <span>图片尺寸</span>
                  <strong>{{ historyImageDimensions(child) }}</strong>
                </div>
                <WorkflowTuningButton
                  v-if="child.result"
                  class="history-group-workflow-button"
                  variant="button"
                  icon-only
                  :image-url="child.result"
                  :prompt="child.prompt || displayedFinalPrompt || finalPrompt"
                  :source-model="child.model || ''"
                  :module-name="featureName"
                />
                <button v-if="child.result" type="button" class="history-group-download" :aria-label="`下载${child.style}结果`" @click="downloadHistoryImage(child)">
                  <i class="el-icon-download"></i>
                </button>
              </div>
            </div>
            <div class="history-info history-group-info">
              <div class="history-group-styles">
                <span v-for="child in record.children" :key="`${child.id}-style`"><i :class="child.status === 'completed' ? 'el-icon-circle-check' : child.status === 'failed' ? 'el-icon-warning-outline' : 'el-icon-loading'"></i>{{ child.style }}</span>
              </div>
              <small class="history-created-at">
                <b>生成时间</b>
                <time class="creation-date" :datetime="record.createdAt || undefined">{{ formatRecordTime(record.createdAt) }}</time>
              </small>
            </div>
          </template>
          <template v-else>
          <div class="history-images">
            <button
              v-if="record.source"
              type="button"
              class="history-thumb"
              title="点击放大历史原图"
              @click="previewImage(record.source, `${record.style} · ${beforeLabel}`, historyViewerItems, historyViewerIndex(record.id, 'source'))"
            >
              <img :src="$ossImageUrl(record.source, 'thumbnail')" alt="历史原图" loading="lazy" decoding="async" />
              <span class="history-image-label">{{ beforeLabel }}</span>
              <i class="el-icon-zoom-in"></i>
            </button>
            <button
              v-if="record.result"
              type="button"
              class="history-thumb"
              :title="`点击放大${featureName}结果`"
              @click="previewImage(record.result, `${record.style} · ${afterLabel}`, historyViewerItems, historyViewerIndex(record.id, 'result'))"
            >
              <img :src="$ossImageUrl(record.result, 'thumbnail')" :alt="`历史${featureName}结果`" loading="lazy" decoding="async" />
              <span class="history-image-label result-label">{{ afterLabel }}</span>
              <i class="el-icon-zoom-in"></i>
            </button>
            <span v-else>{{ statusText(record.status) }}</span>
          </div>
          <div class="history-image-dimension">
            <i class="el-icon-full-screen"></i>
            <span>图片尺寸</span>
            <strong>{{ historyImageDimensions(record) }}</strong>
          </div>
          <div class="history-info">
            <strong>{{ record.style }}</strong>
            <small>{{ record.size }} · {{ statusText(record.status) }}</small>
            <small class="history-created-at">
              <b>生成时间</b>
              <time class="creation-date" :datetime="record.createdAt || undefined">{{ formatRecordTime(record.createdAt) }}</time>
            </small>
            <GenerationModelDetails :record="record" />
            <WorkflowTuningButton
              v-if="record.result"
              class="history-workflow-button"
              variant="button"
              icon-only
              :image-url="record.result"
              :prompt="record.prompt || displayedFinalPrompt || finalPrompt"
              :source-model="record.model || ''"
              :module-name="featureName"
            />
            <small v-if="record.status === 'failed'" class="history-failure-reason" :title="historyFailureReason(record)">
              <i class="el-icon-warning-outline"></i>
              <span>
                <b>失败原因</b>
                <code v-if="historyFailureCode(record)">{{ historyFailureCode(record) }}</code>
                {{ historyFailureReason(record) }}
              </span>
            </small>
          </div>
          <button
            v-if="record.result"
            type="button"
            class="history-download-button"
            title="下载 PNG 图片"
            :aria-label="`下载这条${featureName}结果（PNG）`"
            @click="downloadHistoryImage(record)"
          >
            <i class="el-icon-download"></i>
          </button>
          <button
            type="button"
            class="history-delete-button"
            :disabled="deletingRecordIds.includes(record.id)"
            :title="`删除${featureName}记录`"
            :aria-label="`删除${featureName}记录`"
            @click="removeHistoryRecord(record)"
          >
            <i :class="deletingRecordIds.includes(record.id) ? 'el-icon-loading' : 'el-icon-delete'"></i>
          </button>
          </template>
          <button
            v-if="record.isGroup"
            type="button"
            class="history-delete-button history-group-delete"
            :disabled="deletingRecordIds.includes(record.id)"
            title="删除整组灯光转换记录"
            aria-label="删除整组灯光转换记录"
            @click="removeHistoryGroup(record)"
          >
            <i :class="deletingRecordIds.includes(record.id) ? 'el-icon-loading' : 'el-icon-delete'"></i>
          </button>
        </article>
        </template>
      </div>
      <div v-else class="history-empty">
        <i class="el-icon-time"></i>
        <strong>暂无{{ featureName }}记录</strong>
        <span>新任务提交后会立即显示在这里，无需刷新页面</span>
      </div>
      <AppPagination :page="historyPage" :page-size="12" :total="historyTotal" :loading="historyLoading" @change="changeHistoryPage" />
    </section>
  </section>
</template>

<script>
/* 风格替换保留原图结构和构图，只替换材质、色彩、家具语言等视觉风格。 */
import { mergePendingGenerations, removePendingGeneration, savePendingGeneration } from '@/utils/pendingGenerationRecords'
import { apiRequest } from '@/services/api'
import { downloadGeneratedImageDirectly, downloadImageAsPng, getOriginalImageBlob, originalImageFilename } from '@/utils/imageDownload'
import LightingZoneEditor from '@/components/LightingZoneEditor.vue'
import ResolutionSelector from '@/components/ResolutionSelector.vue'
import QualitySelector from '@/components/QualitySelector.vue'
import AiPromptOptimizer from '@/components/AiPromptOptimizer.vue'
import { DEFAULT_GENERATION_QUALITY, IMAGE_GENERATION_MODEL, MINIMUM_OUTPUT_RESOLUTION, generationPoints, outputDimensions } from '@/utils/outputQuality'
import { isAspectRatioWithinTolerance } from '@/utils/imageAspectRatio'
import { loadImagePixelDimensions } from '@/utils/imagePixelDimensions'

const STYLE_REPLACE_MARKER = '【风格替换任务：原图结构与构图不可改变】'
const SUPPORTED_RATIOS = ['21:9', '16:9', '3:2', '4:3', '5:4', '1:1', '4:5', '3:4', '2:3', '9:16']
const MAX_SOURCE_FILE_BYTES = 10 * 1024 * 1024

export default {
  name: 'StyleReplacement',
  components: { LightingZoneEditor, ResolutionSelector, QualitySelector, AiPromptOptimizer },
  props: {
    lightingMode: {
      type: Boolean,
      default: false
    },
    lightingAdjustment: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sourceFile: null,
      previewUrl: '',
      styleReferenceFile: null,
      styleReferencePreviewUrl: '',
      styleReferenceDragging: false,
      styleReferenceError: '',
      dragging: false,
      fileValidationError: '',
      sourceRatioWarning: '',
      sourceInspectionSerial: 0,
      selectedStyle: '现代简约',
      selectedLightingEffect: 'night-bright',
      selectedLightingEffects: ['night-bright'],
      lightingBrightness: 100,
      writingLightingPrompt: false,
      lightingPromptCooldown: false,
      lightingPromptCooldownTimer: null,
      lightingZones: [],
      activeLightingZoneId: '',
      zoneDrawingEnabled: false,
      nextLightingZoneId: 1,
      restoredTaskStyle: '',
      lightingEffects: [
        {
          id: 'brighten',
          name: '整体灯光提亮',
          caption: '保持现有灯具状态，只提升偏暗画面的照明亮度',
          icon: 'el-icon-sunny',
          tone: 'brighten',
          prompt: '保持原图中所有灯具的种类、数量、位置和开关状态不变，只提升现有人工照明对空间的真实照度与受光层次，使偏暗区域更清晰。不得通过全局曝光、泛白滤镜或抬高自然光来假装提亮，不得造成高光过曝。'
        },
        {
          id: 'indoor',
          name: '室内灯光打开',
          caption: '只打开原图已有灯具，形成温暖柔和的室内光',
          icon: 'el-icon-s-opportunity',
          tone: 'indoor',
          prompt: '只打开原图中已有的室内灯具，呈现温暖、柔和、自然的暖色灯光和真实受光效果。不得新增灯具，保持窗外景色、昼夜时间和室外光线不变。'
        },
        {
          id: 'lights-off',
          name: '关闭全部人工灯光',
          caption: '仅关闭所有灯光 · 其他保持不变',
          icon: 'el-icon-turn-off',
          tone: 'lights-off',
          prompt: '关闭原图中全部人工灯光，包括筒灯、射灯、灯带、吊灯、壁灯、台灯和落地灯产生的发光、光斑、溢光与反射，只保留窗户、玻璃门等带来的自然光。必须完整保留每一件灯具未点亮时的实体、外壳、灯孔、灯罩、轨道和安装点位，禁止删除灯具或改变昼夜环境。'
        },
        {
          id: 'night-bright',
          name: '夜景灯光（明亮）',
          caption: '整体灯光开启 · 80% 亮度',
          brightness: 80,
          nightMode: true,
          icon: 'el-icon-moon-night',
          tone: 'night-bright',
          prompt: '必须先把原图所有真实可见的窗外和对外开口完整转换为深色低照度夜景，彻底消除白昼、黄昏、蓝天、日照和白色天空，再在这个已经成立的夜景基准上打开原图已有的全部人工灯光。人工灯光统一保持正常全亮的 80%，只允许灯具及其真实受光面形成明亮层次；整张图的环境曝光、空间平均亮度和主要墙顶地受光不得高于输入的白天原图，严禁把“明亮”理解为白天式全局提亮、HDR 泛白或用窗外亮光补偿室内。'
        },
        {
          id: 'night-medium',
          name: '夜景灯光（中亮）',
          caption: '整体灯光开启 · 48% 亮度',
          brightness: 48,
          nightMode: true,
          icon: 'el-icon-moon-night',
          tone: 'night-medium',
          prompt: '必须先把原图所有真实可见的窗外和对外开口完整转换为深色低照度夜景，彻底消除白昼、黄昏、蓝天、日照和白色天空，再在这个已经成立的夜景基准上打开原图已有的全部人工灯光。人工灯光统一保持正常全亮的 48%，形成明显低于明亮档、同时高于低亮档的中等夜间层次；整张图的环境曝光、空间平均亮度和主要墙顶地受光必须明显低于输入的白天原图，严禁全局提亮、HDR 泛白或用窗外亮光补偿室内。'
        },
        {
          id: 'night-low',
          name: '夜景灯光（低亮）',
          caption: '整体灯光开启 · 16% 亮度',
          brightness: 16,
          nightMode: true,
          icon: 'el-icon-moon-night',
          tone: 'night-low',
          prompt: '必须先把原图所有真实可见的窗外和对外开口完整转换为深色低照度夜景，彻底消除白昼、黄昏、蓝天、日照和白色天空，再在这个已经成立的夜景基准上打开原图已有的全部人工灯光。人工灯光统一保持正常全亮的 16%，呈现明显偏暗但仍可辨认结构的真实低照度夜景；整张图的环境曝光、空间平均亮度和主要墙顶地受光必须大幅低于输入的白天原图，严禁全局提亮、HDR 泛白或用窗外亮光补偿室内。'
        },
        {
          id: 'night-high-position',
          name: '夜景灯光（高位照明）',
          caption: '只亮顶面射灯、灯带和主灯 · 地面/墙面/柜体灯不亮',
          nightMode: true,
          icon: 'el-icon-top',
          tone: 'night-high-position',
          prompt: '将原图转换为真实夜景。生成前先逐件盘点并锁定原图全部灯具。只点亮原图顶面的射灯、筒灯、灯带和主灯（包括吊灯、吸顶灯），这些高位灯具必须逐件保留、处于点亮状态并清晰可见，严禁因夜景压暗而消失、被吊顶覆盖、缩小、合并或减少数量；地面灯具、墙面灯具、壁灯、台灯、落地灯、柜体灯、踢脚灯和楼梯灯全部不亮。关闭只代表停止发光并移除其光斑、溢光、光晕和对应反射，绝不能删除、隐藏或替换灯具；每件灯具无论点亮或关闭，其实体、外壳、灯孔、灯罩、轨道和安装点位都必须原位完整保留。原图已有的所有窗外必须同步转换为亮度克制的真实夜景，原图没有窗户时禁止新增窗户。'
        },
        {
          id: 'night-low-position',
          name: '夜景灯光（低位照明）',
          caption: '只亮地面/墙面/柜体灯 · 顶面射灯、灯带和主灯不亮',
          nightMode: true,
          icon: 'el-icon-bottom',
          tone: 'night-low-position',
          prompt: '将原图转换为真实夜景。生成前先逐件盘点并锁定原图全部灯具。只点亮原图中的地面灯具、墙面灯具和柜体灯（包括壁灯、台灯、落地灯、柜内灯、踢脚灯和楼梯灯）；顶面的射灯、筒灯、灯带和主灯（包括吊灯、吸顶灯）全部不亮，但所有高位灯具实体仍必须逐件清晰可见，严禁因关闭或夜景压暗而消失、被吊顶覆盖、缩小、合并或减少数量。关闭只代表停止发光并移除其光斑、溢光、光晕和对应反射，绝不能删除、隐藏或替换灯具；每件灯具无论点亮或关闭，其实体、外壳、灯孔、灯罩、轨道和安装点位都必须原位完整保留。原图已有的所有窗外必须同步转换为亮度克制的真实夜景，原图没有窗户时禁止新增窗户。'
        },
        {
          id: 'black-line-art',
          name: '黑底线稿',
          caption: '转换为纯黑背景白色线稿，结构布局完全保持一致',
          icon: 'el-icon-edit-outline',
          tone: 'black-line-art',
          prompt: '把原始效果图转换为严格无彩色的纯黑背景（#000000）、中性纯白线条（#FFFFFF）专业设计线稿，仅抗锯齿边缘允许 R=G=B 的中性灰。严禁暖白、米黄、棕色、金色等有色线条；复杂材质与装饰纹理必须主动简化为稀疏主轮廓，不得继承原图颜色。严格保持原图画幅、镜头、透视、墙顶地边界、门窗、家具、软装和灯具的数量、位置、尺寸、比例、朝向及遮挡关系完全一致；只改变画面的线稿表现形式，不重新设计，不新增、删除、移动或替换任何内容。'
        }
      ],
      selectedRatio: '16:9',
      sourceWidth: 0,
      sourceHeight: 0,
      ratioDetecting: false,
      selectedResolution: MINIMUM_OUTPUT_RESOLUTION,
      selectedQuality: DEFAULT_GENERATION_QUALITY,
      outputCount: 1,
      requirement: '',
      dispatchedPromptPreviews: {},
      promptPreviewLoading: false,
      promptPreviewError: '',
      promptPreviewSerial: 0,
      promptPreviewTimer: null,
      apiConfigured: false,
      generating: false,
      progress: 0,
      generationText: '准备中',
      results: [],
      resultLabels: [],
      currentTaskLabels: [],
      errorMessage: '',
      errorDetails: null,
      currentBatchId: '',
      currentTaskIds: [],
      history: [],
      historyLoading: false,
      historyPage: 1,
      historyTotal: 0,
      deletingRecordIds: [],
      downloadingGroupIds: [],
      requestControllers: [],
      pollingStopped: false,
      pendingRecordTimer: null,
      historyRequestSerial: 0,
      historyLoadErrorNotified: false,
      stylePalettes: [
        ['#f4f2ed', '#c9cdca', '#707973'],
        ['#fff8eb', '#ecd9c3', '#bd9275'],
        ['#efe0c5', '#c89d6b', '#765138'],
        ['#eee9e1', '#beb3a4', '#806846'],
        ['#eee2ce', '#ad8c6f', '#53382f'],
        ['#ded4c7', '#aa9a86', '#696257'],
        ['#faf5ef', '#ddcaca', '#aa858d'],
        ['#cbc4ba', '#79756e', '#2d3432'],
        ['#fbf8ef', '#c9d2c7', '#789181'],
        ['#f0e5ce', '#b8a582', '#625f50'],
        ['#e1d0b8', '#a08365', '#465e54'],
        ['#aaaca6', '#717774', '#303838']
      ],
      styles: [
        { name: '现代简约', caption: '干净利落', swatch: 'linear-gradient(145deg,#f4f2ed 0%,#c9cdca 56%,#707973 100%)', detail: '米白与浅灰为主，简洁线条、克制装饰、利落收口和通透光线' },
        { name: '奶油风', caption: '柔和温暖', swatch: 'linear-gradient(145deg,#fff8eb 0%,#ecd9c3 58%,#bd9275 100%)', detail: '奶油白、米杏和暖木色，圆润家具、柔软织物、低对比度与漫射暖光' },
        { name: '原木风', caption: '自然松弛', swatch: 'linear-gradient(145deg,#efe0c5 0%,#c89d6b 55%,#765138 100%)', detail: '自然浅木纹、米白墙面、棉麻软装、简洁木作和充足自然光' },
        { name: '轻奢风', caption: '精致克制', swatch: 'linear-gradient(145deg,#eee9e1 0%,#beb3a4 54%,#806846 100%)', detail: '高级灰与暖米色，石材、金属细节、精致灯具和克制的奢华质感' },
        { name: '新中式', caption: '东方雅致', swatch: 'linear-gradient(145deg,#eee2ce 0%,#ad8c6f 55%,#53382f 100%)', detail: '温润木色、留白、现代东方家具、格栅与山水意境，沉稳而轻盈' },
        { name: '侘寂风', caption: '质朴静谧', swatch: 'linear-gradient(145deg,#ded4c7 0%,#aa9a86 55%,#696257 100%)', detail: '大地色、微水泥与手作肌理，低饱和配色、自然残缺感和安静光影' },
        { name: '法式风', caption: '浪漫优雅', swatch: 'linear-gradient(145deg,#faf5ef 0%,#ddcaca 57%,#aa858d 100%)', detail: '柔和米白、精细石膏线、弧形家具、黄铜灯具和优雅织物层次' },
        { name: '意式极简', caption: '高级沉稳', swatch: 'linear-gradient(145deg,#cbc4ba 0%,#79756e 55%,#2d3432 100%)', detail: '深灰与暖棕、整块石材、低矮家具、隐形收纳和雕塑感灯具' },
        { name: '北欧风', caption: '清爽明亮', swatch: 'linear-gradient(145deg,#fbf8ef 0%,#c9d2c7 57%,#789181 100%)', detail: '浅色木材、明亮白墙、简洁实用家具、柔和织物与清新点缀色' },
        { name: '日式风', caption: '克制禅意', swatch: 'linear-gradient(145deg,#f0e5ce 0%,#b8a582 56%,#625f50 100%)', detail: '原木、米色与自然纤维，低矮家具、障子元素和克制安静的光线' },
        { name: '美式风', caption: '舒适复古', swatch: 'linear-gradient(145deg,#e1d0b8 0%,#a08365 52%,#465e54 100%)', detail: '温暖木色、复古家具、舒适布艺、细致墙板和有层次的氛围灯光' },
        { name: '工业风', caption: '粗犷个性', swatch: 'linear-gradient(145deg,#aaaca6 0%,#717774 53%,#303838 100%)', detail: '水泥、黑色金属、深色木材、裸露质感和戏剧性的明暗对比' }
      ]
    }
  },
  computed: {
    selectedQualityPoints() {
      return generationPoints(this.selectedQuality)
    },
    taskStage() {
      if (this.lightingAdjustment) return 'lighting_adjustment'
      return this.lightingMode ? 'lighting_conversion' : 'style_replace'
    },
    featureName() {
      if (this.lightingAdjustment) return '灯光调节'
      return this.lightingMode ? '灯光转换' : '风格替换'
    },
    featureAction() {
      if (this.lightingAdjustment) return '灯光调节'
      return this.lightingMode ? '灯光转换' : '替换'
    },
    beforeLabel() {
      if (this.lightingAdjustment) return '调节前'
      return this.lightingMode ? '转换前' : '替换前'
    },
    afterLabel() {
      if (this.lightingAdjustment) return '调节后'
      return this.lightingMode ? '转换后' : '替换后'
    },
    lightingAdjustmentSummary() {
      if (!this.lightingZones.length) return '区域灯光控制'
      return `${this.lightingZones.length} 个区域 · ${this.enabledLightingZoneCount} 处点亮 · ${this.disabledLightingZoneCount} 处关闭`
    },
    enabledLightingZoneCount() {
      return this.lightingZones.filter(zone => zone.state === 'on').length
    },
    disabledLightingZoneCount() {
      return this.lightingZones.filter(zone => zone.state === 'off').length
    },
    lightingZoneInstructions() {
      return this.lightingZones.map((zone, index) => {
        const right = Math.min(100, zone.x + zone.width)
        const bottom = Math.min(100, zone.y + zone.height)
        const action = zone.state === 'on'
          ? `真正点亮既有灯具，亮度为 ${this.lightingBrightness}%；即使原灯当前关闭或灯芯偏暗，也必须形成肉眼可辨的通电发光与小范围自然受光；保留发光面的原始纹理和明暗层次，光线柔和渐变、不过曝，不形成纯白发光块，不增加灯珠或改变灯型`
          : '彻底关闭既有灯具并形成肉眼可辨的断电效果：不得残留白色灯芯或仍像开灯；发光面必须呈现未通电的真实材质状态，同时移除该灯具产生的高光、光晕、光束、洗墙光以及附近表面的对应受光、投影和反射；原灯体、灯孔外径、灯带槽宽与长度、数量、排列、位置和方向必须逐像素对齐，不得缩小、变形、替换或改变灯型'
        return `${index + 1}. 区域 ${index + 1}：画面左上角 (${zone.x.toFixed(1)}%, ${zone.y.toFixed(1)}%) 至右下角 (${right.toFixed(1)}%, ${bottom.toFixed(1)}%)；${action}。`
      }).join('\n')
    },
    taskStyle() {
      if (this.lightingAdjustment) return this.restoredTaskStyle || this.lightingAdjustmentSummary
      if (this.lightingMode) {
        return this.selectedLightingEffectItems.length > 1
          ? `${this.selectedLightingEffectItems.length} 种灯光效果`
          : this.selectedLightingEffectItem.name
      }
      return this.selectedStyle
    },
    selectedStyleItem() {
      return this.styles.find(item => item.name === this.selectedStyle) || this.styles[0]
    },
    selectedLightingEffectItem() {
      return this.lightingEffects.find(item => item.id === this.selectedLightingEffect) || this.lightingEffects[0]
    },
    selectedLightingEffectItems() {
      return this.lightingEffects.filter(item => this.selectedLightingEffects.includes(item.id))
    },
    allLightingEffectsSelected() {
      return this.selectedLightingEffects.length === this.lightingEffects.length
    },
    lightingTaskPrompts() {
      if (!this.lightingMode || this.lightingAdjustment) return []
      return this.selectedLightingEffectItems.map((effect, index) => {
        const sourcePrompt = this.buildLightingConversionPrompt(effect)
        const preview = this.dispatchedPromptPreviews[effect.id]
        const ready = Boolean(preview && preview.sourcePrompt === sourcePrompt && preview.prompt)
        return {
          id: effect.id,
          index: index + 1,
          name: effect.name,
          ready,
          prompt: ready ? preview.prompt : (this.promptPreviewError || '正在从后端同步实际执行提示词…')
        }
      })
    },
    lightingPromptSourceSignature() {
      if (!this.lightingMode || this.lightingAdjustment) return ''
      return JSON.stringify(this.selectedLightingEffectItems.map(effect => ({
        id: effect.id,
        prompt: this.buildLightingConversionPrompt(effect)
      })))
    },
    lightingPromptPreviewsReady() {
      return !this.lightingMode || this.lightingAdjustment || (
        this.lightingTaskPrompts.length > 0 && this.lightingTaskPrompts.every(task => task.ready)
      )
    },
    displayedFinalPrompt() {
      if (!this.lightingMode || this.lightingAdjustment) return this.finalPrompt
      if (this.lightingTaskPrompts.length !== 1) return ''
      return this.lightingTaskPrompts[0].prompt
    },
    finalPrompt() {
      const extra = this.lightingAdjustment
        ? `【结构与材质锁定（最高优先级）】以输入原图作为结构和材质底图，仅允许改变框选灯具的发光状态及其紧邻表面的少量自然受光。发光面仍须保留原始纹理、凹凸与明暗层次，禁止变成纯白实心发光块；光线必须从灯具向外柔和衰减，不能沿选框形成硬边。除紧邻灯具的自然受光外，其余像素保持原图不变，严禁重绘、移动、缩放、变形或替换任何内容。输出必须与原图镜头、透视、消失点、墙顶地边界、家具灯具轮廓完全对齐，禁止任何轻微位置偏移或形变。\n${this.requirement.trim() || '无额外要求'}`
        : (this.requirement.trim() || '无额外要求')
      const ratioNote = this.sourceWidth && this.sourceHeight
        ? (this.lightingMode
            ? `输入原图像素尺寸为 ${this.sourceWidth}×${this.sourceHeight}，输出图像必须保持相同的 ${this.sourceWidth}×${this.sourceHeight} 像素尺寸和 ${this.selectedRatio} 画幅比例，并保持完全相同的横竖方向、裁切范围和构图边界。`
            : `输入原图尺寸为 ${this.sourceWidth}×${this.sourceHeight}，输出画幅必须保持为 ${this.selectedRatio}，保持与原图相同的横竖方向和构图范围。`)
        : '输出画幅必须保持与输入原图相同的宽高比例、横竖方向和构图范围。'
      if (this.lightingMode) {
        if (this.lightingAdjustment) {
          return `只调节干净原图中指定坐标内既有灯具的开关，不重新设计画面。

区域指令：
${this.lightingZoneInstructions || '尚未提供灯光区域。'}

用户补充灯光要求：
${extra}

要求：
          1. 矩形只用于定位灯具，不是光线的硬边界或整体明暗蒙版。点亮时只增加柔和、真实、渐变衰减的局部受光；允许紧邻灯具出现小范围自然光晕，禁止过曝、纯白发光块、大片光柱、矩形亮斑和整屋提亮；滑杆百分比是亮度上限。
          2. 执行“关闭”时必须把对应灯具视为断电状态：发光面不得继续呈白色亮点或自发光，必须同时移除由它产生的光晕、光束、洗墙光以及附近材质上的对应高光、受光和反射；只移除该灯具产生的光，不得把自然光或其他未选灯光一起压暗。
          3. 灯具轮廓、灯孔、灯珠数量与排列、尺寸、位置、方向必须和原图逐像素一致；尤其不得因关闭灯光而缩小灯芯、改变灯孔外径、灯带槽宽与长度，禁止新增、删除、替换或改变灯型。
          4. 除所选灯具及其真实受光外，框外像素、未选灯光、昼夜、自然光、白平衡、结构、材质、家具保持原图不变；${ratioNote}
          5. 输出与原图逐像素对齐，只输出一张写实图，不要框线、遮罩、编号、文字、水印或拼图。`
        }
        return this.selectedLightingEffectItems.length === 1
          ? this.buildLightingConversionPrompt(this.selectedLightingEffectItems[0])
          : ''
      }
      const referenceNote = this.styleReferenceFile
        ? '输入共两张图片：第一张是必须严格保留结构与构图的原有效果图；第二张是风格参考图，只提取其配色、材质、纹理、家具造型语言和灯光氛围。严禁复制第二张图的户型、门窗、镜头、构图、家具位置或物体数量。'
        : '输入只有一张原有效果图，以所选目标风格和文字要求完成替换。'
      return `${STYLE_REPLACE_MARKER}\n任务类型：对输入原图进行高保真风格替换，不是重新设计空间。\n${referenceNote}\n${ratioNote}\n目标风格：${this.selectedStyle}。视觉特征：${this.selectedStyleItem.detail}。该风格描述只用于指导材质、色彩、表面细节与光线氛围，不代表可以改变原有物体的形态、尺寸或位置。\n用户补充：${extra}。\n\n【最高优先级保留项】\n1. 完整保留原图的宽高比例、横竖方向、裁切范围、镜头高度、拍摄角度、透视关系、消失点和整体构图，不得拉伸、压缩、扩图或改为其他画幅。\n2. 完整保留原图的空间结构与几何边界，包括墙面、地面、顶面、梁、柱子、门窗、开口、吊顶层级和固定硬装的位置、尺寸、厚度及比例。\n3. 完整保留原图中全部硬装与软装元素的数量、准确位置、朝向、大小、比例、占地范围、前后遮挡关系和大致轮廓，包括家具、灯具、窗帘、地毯、挂画及摆件。\n4. 不得新增、删除、移动、旋转、缩放或替换任何建筑构件、家具及软装，不得改变空间功能、家具布局或画面内容。\n\n【允许修改的范围】\n仅允许在不改变原有轮廓、大小和布局的前提下，调整现有墙顶地、硬装、家具与软装的材质、颜色、表面纹理、饰面、收口细节和光线氛围，使画面明确呈现${this.selectedStyle}。\n如果风格表达与原图保真发生冲突，必须以保留原图画幅、结构、构图、元素位置、大小和大致形态为最高优先级。\n\n输出单张真实、符合美学、专业渲染的写实室内效果图。不要输出前后对比拼图，不要文字、标签、水印或边框。`
    },
    resultSlots() {
      const expected = this.lightingMode && !this.lightingAdjustment
        ? (this.currentTaskLabels.length || this.selectedLightingEffectItems.length)
        : Number(this.outputCount)
      const length = Math.max(expected, this.results.length, 1)
      return Array.from({ length }, (item, index) => this.results[index] || '')
    },
    historyDisplayRecords() {
      if (!this.lightingMode || this.lightingAdjustment) return this.history
      const entries = []
      const groups = new Map()
      this.history.forEach(record => {
        const groupKey = this.lightingHistoryGroupKey(record)
        if (!groupKey) {
          entries.push({ type: 'single', record })
          return
        }
        if (!groups.has(groupKey)) {
          const group = { type: 'group', key: groupKey, records: [] }
          groups.set(groupKey, group)
          entries.push(group)
        }
        groups.get(groupKey).records.push(record)
      })
      const effectOrder = new Map(this.lightingEffects.map((effect, index) => [effect.name, index]))
      return entries.map(entry => {
        if (entry.type === 'single') return entry.record
        const children = [...entry.records].sort((left, right) => (
          (effectOrder.get(left.style) ?? 99) - (effectOrder.get(right.style) ?? 99)
        ))
        if (children.length < 2) return children[0]
        const first = children[0]
        const statuses = children.map(child => child.status)
        const status = statuses.every(item => item === 'completed')
          ? 'completed'
          : statuses.every(item => item === 'failed')
            ? 'failed'
            : statuses.some(item => ['generating', 'pending'].includes(item))
              ? 'generating'
              : 'partial'
        return {
          ...first,
          id: `lighting-group:${entry.key}`,
          groupKey: entry.key,
          isGroup: true,
          children,
          style: `${children.length} 种灯光效果`,
          status,
          taskIds: children.flatMap(child => child.taskIds || []),
          taskStates: children.flatMap(child => child.taskStates || []),
          createdAt: children.reduce((latest, child) => (
            new Date(child.createdAt || 0).getTime() > new Date(latest || 0).getTime() ? child.createdAt : latest
          ), first.createdAt)
        }
      })
    },
    historySectionRecords() {
      // historyDisplayRecords already follows the database's newest-first order.
      // Keep single tasks in that order instead of pushing them below every batch,
      // otherwise a newly generated one-off effect appears to be missing.
      return this.historyDisplayRecords
    },
    currentViewerItems() {
      const items = []
      if (this.previewUrl) {
        items.push({
          src: this.previewUrl,
          title: `${this.featureName} · ${this.beforeLabel}`,
          key: 'current-source'
        })
      }
      this.results.filter(Boolean).forEach((src, index) => {
        items.push({
          src,
          title: `${this.featureName} · ${this.afterLabel} · ${this.resultStyle(index)}`,
          key: `current-result-${index}`,
          fastDownloadTaskId: this.lightingAdjustment ? (this.currentTaskIds[index] || '') : ''
        })
      })
      return items
    },
    historyViewerItems() {
      return this.history.reduce((items, record) => {
        if (record.source) {
          items.push({
            src: record.source,
            title: `${record.style} · ${this.beforeLabel}`,
            key: `${record.id}-source`
          })
        }
        if (record.result) {
          items.push({
            src: record.result,
            title: `${record.style} · ${this.afterLabel}`,
            key: `${record.id}-result`,
            fastDownloadTaskId: this.lightingAdjustment && Array.isArray(record.taskIds)
              ? (record.taskIds.find(Boolean) || '')
              : ''
          })
        }
        return items
      }, [])
    }
  },
  watch: {
    lightingPromptSourceSignature: {
      immediate: true,
      handler() { this.scheduleLightingPromptPreviewSync() }
    }
  },
  created() {
    this.loadConfiguration()
    this.loadHistory()
  },
  beforeDestroy() {
    this.pollingStopped = true
    if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
    if (this.promptPreviewTimer) window.clearTimeout(this.promptPreviewTimer)
    if (this.lightingPromptCooldownTimer) window.clearTimeout(this.lightingPromptCooldownTimer)
    this.requestControllers.forEach(controller => controller.abort())
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
    if (this.styleReferencePreviewUrl) URL.revokeObjectURL(this.styleReferencePreviewUrl)
  },
  methods: {
    scheduleLightingPromptPreviewSync() {
      if (!this.lightingMode || this.lightingAdjustment) return
      if (this.promptPreviewTimer) window.clearTimeout(this.promptPreviewTimer)
      this.promptPreviewTimer = window.setTimeout(() => {
        this.promptPreviewTimer = null
        this.refreshLightingPromptPreviews().catch(() => {})
      }, 180)
    },
    async refreshLightingPromptPreviews(strict = false) {
      if (!this.lightingMode || this.lightingAdjustment) return true
      const entries = this.selectedLightingEffectItems.map(effect => ({
        id: effect.id,
        sourcePrompt: this.buildLightingConversionPrompt(effect)
      }))
      if (!entries.length) return true
      const serial = ++this.promptPreviewSerial
      this.promptPreviewLoading = true
      this.promptPreviewError = ''
      try {
        const results = await Promise.all(entries.map(async entry => {
          const response = await this.request('/api/prompts/image-dispatch-preview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt: entry.sourcePrompt, stage: 'lighting_conversion' }),
            timeoutMs: 30000,
            retries: 1,
            timeoutMessage: '同步实际执行提示词超时'
          })
          const prompt = String(response.prompt || '').trim()
          if (!prompt) throw new Error('后端未返回实际执行提示词')
          return { ...entry, prompt }
        }))
        if (serial !== this.promptPreviewSerial) return false
        const previews = {}
        results.forEach(result => { previews[result.id] = result })
        this.dispatchedPromptPreviews = previews
        return true
      } catch (error) {
        if (serial === this.promptPreviewSerial) {
          this.promptPreviewError = '实际执行提示词同步失败，请稍后重试'
        }
        if (strict) throw error
        return false
      } finally {
        if (serial === this.promptPreviewSerial) this.promptPreviewLoading = false
      }
    },
    resetLightingAdjustmentResult() {
      if (!this.lightingAdjustment || this.generating) return
      this.restoredTaskStyle = ''
      this.results = []
      this.resultLabels = []
      this.currentTaskLabels = []
      this.errorMessage = ''
      this.errorDetails = null
      this.currentBatchId = ''
      this.currentTaskIds = []
    },
    openLightingFilePicker() {
      if (this.generating || !this.$refs.lightingFileInput) return
      this.$refs.lightingFileInput.click()
    },
    startZoneDrawing() {
      if (!this.previewUrl) {
        this.openLightingFilePicker()
        return
      }
      this.zoneDrawingEnabled = true
    },
    createLightingZone(rectangle) {
      if (this.generating) return
      const zone = {
        id: `light-zone-${this.nextLightingZoneId}`,
        x: Number(rectangle.x.toFixed(2)),
        y: Number(rectangle.y.toFixed(2)),
        width: Number(rectangle.width.toFixed(2)),
        height: Number(rectangle.height.toFixed(2)),
        state: 'on'
      }
      this.nextLightingZoneId += 1
      this.lightingZones = [...this.lightingZones, zone]
      this.activeLightingZoneId = zone.id
      this.resetLightingAdjustmentResult()
    },
    selectLightingZone(zoneId) {
      this.activeLightingZoneId = zoneId
      this.$nextTick(() => {
        const target = this.$refs[`lightingZoneControl-${zoneId}`]
        const element = Array.isArray(target) ? target[0] : target
        if (element && typeof element.scrollIntoView === 'function') {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    setLightingZoneState(zoneId, state) {
      if (this.generating) return
      this.lightingZones = this.lightingZones.map(zone => (
        zone.id === zoneId ? { ...zone, state } : zone
      ))
      this.activeLightingZoneId = zoneId
      this.resetLightingAdjustmentResult()
    },
    removeLightingZone(zoneId) {
      if (this.generating) return
      this.lightingZones = this.lightingZones.filter(zone => zone.id !== zoneId)
      if (this.activeLightingZoneId === zoneId) {
        this.activeLightingZoneId = this.lightingZones.length ? this.lightingZones[0].id : ''
      }
      this.resetLightingAdjustmentResult()
    },
    clearLightingZones() {
      if (this.generating) return
      this.lightingZones = []
      this.activeLightingZoneId = ''
      this.zoneDrawingEnabled = false
      this.resetLightingAdjustmentResult()
    },
    zonePositionText(zone) {
      const centerX = zone.x + zone.width / 2
      const centerY = zone.y + zone.height / 2
      const horizontal = centerX < 34 ? '左侧' : centerX > 66 ? '右侧' : '中部'
      const vertical = centerY < 34 ? '上方' : centerY > 66 ? '下方' : '中部'
      return horizontal === '中部' && vertical === '中部' ? '中央' : `${vertical}${horizontal}`
    },
    normalizeLightingBrightness() {
      const value = Number(this.lightingBrightness)
      this.lightingBrightness = Number.isFinite(value) ? Math.min(100, Math.max(20, Math.round(value))) : 100
      this.resetLightingAdjustmentResult()
    },
    selectLightingEffect(effectId) {
      if (this.generating) return
      const selected = this.selectedLightingEffects.includes(effectId)
      this.selectedLightingEffects = selected
        ? this.selectedLightingEffects.filter(id => id !== effectId)
        : [...this.selectedLightingEffects, effectId]
      this.selectedLightingEffect = this.selectedLightingEffects[0] || ''
      this.results = []
      this.resultLabels = []
      this.currentTaskLabels = []
      this.errorMessage = ''
      this.errorDetails = null
    },
    toggleAllLightingEffects() {
      if (this.generating) return
      if (this.allLightingEffectsSelected) {
        this.selectedLightingEffects = []
        this.selectedLightingEffect = ''
      } else {
        this.selectedLightingEffects = this.lightingEffects.map(item => item.id)
        this.selectedLightingEffect = this.selectedLightingEffects[0]
      }
      this.results = []
      this.resultLabels = []
      this.currentTaskLabels = []
      this.errorMessage = ''
      this.errorDetails = null
    },
    resultStyle(index) {
      return this.resultLabels[index] || this.selectedLightingEffectItems[index]?.name || this.taskStyle
    },
    buildLightingConversionPrompt(effect) {
      const extra = this.requirement.trim() || '无额外要求'
      const structuredLightingIds = ['lights-off', 'night-high-position', 'night-low-position']
      const structuredRatioNote = this.sourceWidth && this.sourceHeight
        ? `输入原图尺寸为 ${this.sourceWidth}×${this.sourceHeight}，输出画幅必须保持为 ${this.selectedRatio}，保持与原图相同的横竖方向和构图范围。`
        : '输出画幅必须保持与输入原图相同的宽高比例、横竖方向和构图范围。'
      if (structuredLightingIds.includes(effect.id)) {
        const positionFixtureLock = ['night-high-position', 'night-low-position'].includes(effect.id)
          ? `\n灯具实体复核（最高优先级）：先记录原图每一件高位、墙面、地面和柜体灯具的数量与坐标，输出前逐件对照。点亮的灯具必须保留原实体并明确可见；关闭的灯具只能去除发光和受光，灯体仍须原位可见。任何灯具都不得因切换开关、夜景压暗或吊顶重绘而消失、隐藏、合并、缩小、减少数量或改变灯型。\n`
          : ''
        return `对输入原图进行“${effect.name}”转换。

保持原图不变：保留原有构图、镜头、空间结构、家具摆放、装修风格、材质和画面比例，不新增、删除、移动或替换任何物体。

目标效果：${effect.prompt}
${positionFixtureLock}

${structuredRatioNote}
只执行以上选中的一种灯光效果，不叠加其他灯光效果，不改变原图中的其他内容。不要文字、水印、边框或前后对比图。
补充要求：${extra}。`
      }
      const isNightPreset = Number.isFinite(effect.brightness)
      const isNightMode = effect.nightMode === true
      const nightSceneStructureRule = isNightMode
        ? `【夜景结构零改动规则｜强制执行】
1. 先以输入原图为唯一证据，逐一盘点并内部编号所有门、窗、玻璃门、洞口和其他对外开口；输出中的开口数量必须与原图严格相等，原图中不存在的结构数量必须保持为 0。
2. 原图每一处真实可见的窗户或对外开口，其内部所有可见室外区域都必须完整转换成夜景，任何一扇窗、任何一块窗格都不得遗漏或残留白昼、黄昏、蓝天、日照、白色天空。只改变开口内部原本可见室外区域的昼夜光照，不得扩大、移动、变形、替换开口或改动窗框、玻璃分格。
3. 如果原图没有窗户或对外开口，必须保持窗户和对外开口数量严格为 0，并保持墙面、柜体、饰面、装饰画和全部遮挡关系连续完整。严禁为了表现“夜景”凿开或生成窗户、落地窗、玻璃门、门洞、采光井、墙缝、蓝黑色开口、天空、城市建筑、灯光天际线或任何室外景观；也不得把反射、高光、阴影或深色矩形误画成窗户。
4. 夜景只能通过调整原图已有光源、已有自然光入口及其在既有表面上的受光、阴影、反射和明暗衰减来表达。不得把墙面或柜体局部压成黑色/蓝色矩形来伪装窗户。
5. 原图已有窗户或对外开口时，每一处开口内部可见的天空、建筑和景物都必须统一转换为低照度真实夜景：天空应为深色，室外建筑与环境光必须克制，允许少量远处点状灯光，但不得出现白色天空、蓝天、日照感、大面积高亮建筑或发白光幕。多扇窗必须处于同一夜间时段与一致的室外曝光，禁止部分是夜景、部分仍是白昼。
6. 所有窗外可见区域的整体亮度与视觉权重必须明显低于室内主要受光面，不得抢过室内照明主体；玻璃上的反射和高光也必须受控，严禁通过提高窗外曝光来照亮室内。
7. 高位照明与低位照明同样属于夜景模式，必须完整执行以上窗外低照度规则；不得仅切换灯具开关而保留白昼窗外。
8. 提示词中的“窗外”“室外”只对原图确实存在的可见室外区域生效；原图不存在此类区域时，必须忽略相关外景变化描述，绝不通过新增窗户或室外景观来表达夜景，优先保持原图结构和内容。`
        : ''
      const fixtureInventoryRule = `【灯具实体清单锁定｜最高优先级】
1. 生成前必须以输入原图为唯一证据，逐类盘点吊灯、吸顶灯、筒灯、射灯、轨道灯、灯带、壁灯、台灯、落地灯及其他灯具实体；原图中某类灯具不存在时，该类灯具在输出中的数量必须严格保持为 0。
2. 输出中每一件灯具实体必须能在原图相同坐标找到同一件实体，且数量、类型、外形、尺寸、方向、安装位置和安装结构完全一致。找不到原图对应物的灯具一律禁止生成。
3. 绝对禁止新增轨道、轨道灯头、射灯头、筒灯孔、灯带、吊线、灯罩、灯座或任何发光装置；禁止复制、延长、补齐或阵列原有灯具。
4. 不得把吊顶边界、灯槽、阴影缝、装饰线、黑色线条、反射、高光或透视线误识别、加粗或重绘为灯轨、灯带、灯孔或灯头。
5. “夜景”只允许由原图已有灯具与既有表面的受光变化表达。即使新增灯具能让画面更亮、更合理或更美观，也必须放弃新增；灯具保真优先于灯光效果。
6. 输出前必须逐项反查所有灯具：凡是原图没有对应实体的新增灯具，必须删除该新增内容并恢复原图该位置的顶面、墙面、柜体或装饰面。`
      const nightBrightnessRule = isNightPreset
        ? `【夜景整体灯光预设｜强制执行】
1. 执行顺序不可颠倒：第一步先完成全部窗外和对外开口的低照度夜景转换并锁定深色室外曝光；第二步才允许开启室内人工灯光；不得一边保留白昼窗外一边调整室内灯光，也不得先提亮全图再套夜景色调。
2. 根据输入原图盘点实际存在的全部人工灯具，不得臆造或遗漏射灯、灯带、吊灯、吸顶灯、壁灯、台灯、落地灯及其他已有灯具。原图中已有的全部人工灯光必须处于开启状态，不得新增原图中不存在的灯具。
3. 所有已有人工灯光统一固定为正常全亮的 ${effect.brightness}%；该比例同时适用于每一类灯具，不按灯具类型分别设置，并且只控制人工灯具及其真实受光，绝不代表整张图片的曝光百分比。
4. ${effect.brightness}% 必须真实反映在灯具发光面、受光区域、光斑、反射、阴影和自然衰减中，灯光亮度与空间受光强度必须一致。
5. 三档必须建立在同一个真实夜景基准上并形成稳定梯度：80% 为夜景明亮、48% 为夜景中亮、16% 为夜景低亮；当前任务只执行 ${effect.brightness}% 这一档。
6. 输出整张图的环境平均亮度必须低于输入的白天原图；主要墙面、顶面、地面和家具受光不得比白天原图更亮。即使是 80% 明亮档，也只允许灯芯和小范围直接受光形成亮点，绝不允许整屋比白天更亮。
7. 严禁提高全局曝光、环境光、自然光、窗外亮度、暗部或白平衡来抵消夜景与当前亮度预设；禁止 HDR 泛白、白雾、发灰黑位和用窗外亮光补偿室内。
8. 本预设亮度优先于用户补充要求中的冲突亮度描述，但不得改变任何灯具的数量、类型、尺寸、位置、外形和原图构图。`
        : ''
      const ratioNote = this.sourceWidth && this.sourceHeight
        ? `输入原图像素尺寸为 ${this.sourceWidth}×${this.sourceHeight}，输出图像必须保持相同的 ${this.sourceWidth}×${this.sourceHeight} 像素尺寸和 ${this.selectedRatio} 画幅比例，并保持完全相同的横竖方向、裁切范围和构图边界。`
        : '输出画幅必须保持与输入原图相同的宽高比例、横竖方向和构图范围。'
      if (effect.id === 'black-line-art') {
        return `【黑底线稿任务｜原图内容和几何完全锁定｜最高优先级】
任务唯一目标：把输入的单张室内效果图转换为严格无彩色的纯黑背景（#000000）、中性纯白线条（#FFFFFF）专业设计线稿；仅抗锯齿边缘允许 R=G=B 的中性灰。严禁暖白、米黄、奶油色、棕色、金色等任何有色线条。复杂材质和装饰纹理必须主动简化为稀疏主轮廓，宁可省略次要纹理，也不得继承原图颜色、生成密集交叉线或照片质感。只允许改变画面的视觉表现形式，严禁改变任何内容、结构、构图或物体位置。

目标效果：${effect.prompt}

【必须与原图完全一致】
1. ${ratioNote} 输出画布不得裁切、扩图、补边、拉伸、压缩、旋转或翻转。
2. 镜头机位、拍摄角度、透视关系、消失点、构图边界以及墙、地、顶、梁柱、门窗、柜体和吊顶分缝必须与原图逐项对齐。
3. 家具、灯具、窗帘、地毯、挂画、饰品、植物及全部物体的数量、位置、尺寸、比例、朝向、轮廓和遮挡关系必须完整保留。
4. 硬装使用清晰主轮廓和少量必要分缝；家具与软装只保留准确外轮廓和必要结构线，避免无意义细纹和过度描边。
5. 灯具只表达实体轮廓、灯孔或安装点位；不得绘制光束、光斑、照明范围、溢光、反射光、发光雾、亮度渐变或光晕。
6. 禁止重新设计，禁止新增、删除、移动、旋转、缩放、变形或替换任何内容；禁止文字、尺寸、标注、水印、边框和拼图。

输出一张能够与原图直接重叠核对构图和物体位置的完整黑底白线稿，只输出最终图片。
用户补充要求：${extra}。`
      }
      return `【灯光转换任务｜原图是不可变底图｜最高优先级】
任务唯一目标：对输入原图执行“${effect.name}”，只允许改变灯光的开启状态、照度、色温以及灯光自然产生的受光、阴影、反射和明暗衰减；除此之外画面任何内容都不得变化。

目标灯光效果：${effect.prompt}
${fixtureInventoryRule}
${nightSceneStructureRule ? `\n${nightSceneStructureRule}\n` : ''}
${nightBrightnessRule ? `\n${nightBrightnessRule}\n` : ''}

【必须与原图完全一致】
1. ${ratioNote} 输出画布不得裁切、扩图、补边、拉伸、压缩、旋转、翻转或改变横竖方向。
2. 镜头机位、焦距观感、拍摄角度、透视关系、消失点、构图范围和每条画面边界必须与原图逐像素对齐，不得出现任何缩放或位置偏移。
3. 墙、地、顶、梁柱、门窗、洞口、柜体、吊顶、分缝及全部固定硬装的数量、位置、尺寸、比例、形状、轮廓、材质和颜色必须完全一致；原图没有的门、窗或洞口必须保持为 0，绝对禁止新增。
4. 家具、灯具、窗帘、地毯、挂画、饰品、植物及画面中所有物体的数量、位置、大小、朝向、轮廓、遮挡关系、材质和颜色必须完全一致。
5. 禁止新增、删除、移动、旋转、缩放、变形、替换、美化或重新设计任何物体；禁止改变装修风格、家具布局、空间功能、窗外建筑和景物。
6. 灯具实体必须完整保留。只有目标效果明确要求改变开关状态时，才可改变原图已有灯具的发光状态；目标效果要求保持开关状态时必须原样保持。任何开关操作都只改变真实发光与受光效果，不得删除、移动、修改或新增灯孔、边框、灯罩、灯带、轨道、灯头、吊线和安装底座。
7. 不得通过重绘全图、简单曝光、亮色/暗色滤镜、整体泛白或全局调色伪造灯光效果。未受目标灯光影响的区域应尽量直接继承原图像素。
8. 如果目标灯光效果、用户补充要求与原图保真发生任何冲突，必须无条件优先保持原图尺寸、构图、结构、物体位置和画面内容不变。

只执行上述一种灯光变化，不叠加其他效果。输出一张与原图可直接重叠对比的完整写实效果图，不要拼图、文字、标签、水印或边框。
用户补充要求：${extra}。`
    },
    async request(url, options = {}) {
      return apiRequest(url, options, {
        controllers: this.requestControllers,
        isStopped: () => this.pollingStopped
      })
    },
    async loadConfiguration() {
      try {
        const result = await this.request('/api/health')
        this.apiConfigured = Boolean(result.configured)
      } catch (error) {
        this.apiConfigured = false
      }
    },
    async loadHistory() {
      const requestSerial = ++this.historyRequestSerial
      this.historyLoading = true
      try {
        const response = await this.request(`/api/records/paged?stage=${encodeURIComponent(this.taskStage)}&page=${this.historyPage}&pageSize=12`)
        if (requestSerial !== this.historyRequestSerial) return
        this.historyTotal = Number(response.total || 0)
        const records = response.items || []
        const taskStage = this.taskStage
        const mergedRecords = this.historyPage === 1 ? mergePendingGenerations(records, taskStage) : records
        let nextHistory = mergedRecords.filter(item => {
          if (item.stage === taskStage) return true
          if (!this.lightingAdjustment || item.stage !== 'concept') return false
          if (String(item.id || '').startsWith('interior-lighting-adjustment-')) return true
          const style = String(item.style || '')
          const prompt = String(item.prompt || '')
          return prompt.includes('灯光亮度调节任务')
            || prompt.includes('照明亮度调整')
            || (/照画灯|灯带/.test(style) && String(item.space || '') === '原有效果图')
        }).map(item => this.normalizeHistoryRecord(item))
        if (this.lightingMode && !this.lightingAdjustment) {
          nextHistory = await this.completeLightingHistoryGroups(nextHistory)
          if (requestSerial !== this.historyRequestSerial) return
        }
        const visibleIds = new Set(nextHistory.map(item => item.id))
        const optimisticRecords = this.history.filter(item => (
          item && !visibleIds.has(item.id) &&
          (item.id === this.currentBatchId || item.status === 'generating')
        ))
        this.history = [...optimisticRecords, ...nextHistory]
          .sort((left, right) => {
            const rightTime = new Date(right.createdAt || 0).getTime() || 0
            const leftTime = new Date(left.createdAt || 0).getTime() || 0
            return rightTime - leftTime
          })
          .slice(0, this.lightingMode && !this.lightingAdjustment ? 48 : 12)
        this.populateHistoryImageDimensions(this.history)
        const activeRecord = this.history.find(item => item.taskIds.length && item.taskStates.some(task => !['completed', 'failed'].includes(task.status)))
        if (activeRecord && !this.generating && !this.currentTaskIds.length) {
          if (this.lightingMode && !this.lightingAdjustment) {
            const savedEffect = this.lightingEffects.find(item => item.name === activeRecord.style)
            if (savedEffect) {
              this.selectedLightingEffect = savedEffect.id
              this.selectedLightingEffects = [savedEffect.id]
              this.resultLabels = [savedEffect.name]
              this.currentTaskLabels = [savedEffect.name]
            }
          }
          this.currentBatchId = activeRecord.id
          this.currentTaskIds = activeRecord.taskIds
          if (!this.lightingAdjustment && !this.previewUrl && activeRecord.source) this.previewUrl = activeRecord.source
          this.continuePolling(true)
        }
        this.historyLoadErrorNotified = false
        this.schedulePendingRecordRefresh()
      } catch (error) {
        if (!this.history.length) this.history = []
        if (!this.historyLoadErrorNotified) {
          this.historyLoadErrorNotified = true
          this.showMessage(`${error.message || '暂时无法加载生成记录'}。${error.suggestion || '生成主流程仍可使用，请稍后点击刷新记录。'}`, 'warning')
        }
      } finally {
        if (requestSerial === this.historyRequestSerial) this.historyLoading = false
      }
    },
    schedulePendingRecordRefresh() {
      if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
      if (this.pollingStopped || !this.history.some(record => record.status === 'generating')) return
      this.pendingRecordTimer = window.setTimeout(() => {
        this.pendingRecordTimer = null
        this.loadHistory()
      }, 2000)
    },
    normalizeHistoryRecord(item) {
      return {
        id: item.id,
        style: item.style,
        size: item.size || '16:9',
        status: item.status,
        prompt: item.prompt || '',
        errorMessage: item.errorMessage || item.error || '',
        imageModel: item.imageModel || '',
        taskIds: (item.tasks || []).map(task => task.id).filter(Boolean),
        taskStates: item.tasks || [],
        expectedCount: Math.max(Number(item.expectedCount || 0), (item.tasks || []).length, 1),
        createdAt: item.createdAt || item.updatedAt || '',
        updatedAt: item.updatedAt || '',
        source: item.inputContext && item.inputContext.referenceImages && item.inputContext.referenceImages[0]
          ? item.inputContext.referenceImages[0].url
          : '',
        result: item.images && item.images[0] ? (item.images[0].url || item.images[0]) : '',
        imageDimensions: item.images && item.images[0] && item.images[0].width && item.images[0].height
          ? `${item.images[0].width} × ${item.images[0].height}`
          : ''
      }
    },
    historyImageDimensions(record) {
      if (record && record.imageDimensions) return record.imageDimensions
      return '无'
    },
    historySourceImageDimensions(record) {
      if (record && record.sourceImageDimensions) return record.sourceImageDimensions
      return '无'
    },
    populateHistoryImageDimensions(records) {
      ;(records || []).forEach(record => {
        if (!record) return
        if (record.source && !record.sourceImageDimensions) {
          loadImagePixelDimensions(record.source).then(value => {
            if (value && !this.pollingStopped) this.$set(record, 'sourceImageDimensions', value)
          })
        }
      })
    },
    lightingGroupExpectedEffectIds(groupKey) {
      const match = String(groupKey || '').match(/lighting-batch-m([0-9a-f]+)-/i)
      if (!match) return []
      const mask = Number.parseInt(match[1], 16)
      if (!Number.isFinite(mask)) return []
      return this.lightingEffects
        .filter((effect, index) => (mask & (1 << index)) !== 0)
        .map(effect => effect.id)
    },
    async completeLightingHistoryGroups(records) {
      const nextRecords = [...records]
      const presentIds = new Set(nextRecords.map(record => record.id))
      const groupKeys = [...new Set(nextRecords.map(record => this.lightingHistoryGroupKey(record)).filter(Boolean))]
      const missingIds = groupKeys.flatMap(groupKey => (
        this.lightingGroupExpectedEffectIds(groupKey)
          .map(effectId => `${groupKey}--${effectId}`)
          .filter(id => !presentIds.has(id))
      ))
      if (!missingIds.length) return nextRecords

      const recovered = await Promise.allSettled(missingIds.map(id => (
        this.request(`/api/records/${encodeURIComponent(id)}`, {
          timeoutMs: 12000,
          retries: 0,
          timeoutMessage: '补全批量灯光记录超时'
        })
      )))
      recovered.forEach(item => {
        if (item.status !== 'fulfilled' || !item.value || presentIds.has(item.value.id)) return
        presentIds.add(item.value.id)
        nextRecords.push(this.normalizeHistoryRecord(item.value))
      })
      return nextRecords
    },
    lightingHistoryGroupKey(record) {
      const id = String(record && record.id ? record.id : '')
      const separatorIndex = id.lastIndexOf('--')
      if (separatorIndex < 0 || !id.includes('lighting-batch-')) return ''
      const effectId = id.slice(separatorIndex + 2)
      return this.lightingEffects.some(effect => effect.id === effectId)
        ? id.slice(0, separatorIndex)
        : ''
    },
    groupStatusText(record) {
      if (!record || !record.children) return ''
      const completed = record.children.filter(child => child.status === 'completed').length
      const failed = record.children.filter(child => child.status === 'failed').length
      if (completed === record.children.length) return `全部完成 · ${completed}/${record.children.length}`
      if (failed === record.children.length) return `全部失败 · ${failed}/${record.children.length}`
      if (failed) return `已完成 ${completed} · 失败 ${failed}`
      return `生成中 · ${completed}/${record.children.length}`
    },
    changeHistoryPage(page) {
      this.historyPage = page
      this.loadHistory()
    },

    async waitForSubmittedTasks(batchId, expectedCount) {
      const deadline = Date.now() + 120000
      let consecutiveErrors = 0
      while (Date.now() < deadline && !this.pollingStopped) {
        try {
          const record = await this.request(`/api/records/${encodeURIComponent(batchId)}`, {
            timeoutMs: 12000,
            retries: 1,
            timeoutMessage: '确认后台任务编号超时'
          })
          consecutiveErrors = 0
          const tasks = Array.isArray(record.tasks) ? record.tasks : []
          const taskIds = tasks.map(task => task && task.id).filter(Boolean)
          const historyRecord = this.history.find(item => item.id === batchId)
          if (historyRecord) {
            historyRecord.status = record.status || historyRecord.status
            historyRecord.taskIds = taskIds
            historyRecord.taskStates = tasks
            historyRecord.createdAt = record.createdAt || record.updatedAt || historyRecord.createdAt
            historyRecord.updatedAt = record.updatedAt || historyRecord.updatedAt
          }
          if (record.status === 'failed') {
            const failedTask = tasks.find(task => task && task.status === 'failed')
            const details = failedTask && failedTask.error ? failedTask.error : {}
            const error = new Error(details.message || record.errorMessage || `${this.featureName}任务提交失败`)
            Object.assign(error, details, { terminal: true, retryable: details.retryable !== false })
            throw error
          }
          if (taskIds.length >= expectedCount) return { taskIds, tasks }
          this.progress = Math.max(this.progress, 16)
          this.generationText = '任务已保存，正在等待 AI 服务接收'
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error
          consecutiveErrors += 1
          this.generationText = `任务已保存，正在恢复连接（${consecutiveErrors}）`
        }
        await new Promise(resolve => window.setTimeout(resolve, 2000))
      }
      const pending = new Error('任务已保存，后台仍在确认 AI 任务编号')
      pending.code = 'TASK_ID_PENDING'
      pending.suggestion = '系统会自动继续同步；您可以离开页面，稍后在历史记录中查看。'
      pending.pending = true
      pending.retryable = true
      throw pending
    },
    async removeHistoryRecord(record) {
      if (this.deletingRecordIds.includes(record.id)) return
      try {
        await this.$confirm(
          `删除这条${this.featureName}记录后将无法恢复，是否继续？`,
          '删除创作记录',
          {
            confirmButtonText: '删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
      } catch (error) {
        return
      }

      this.deletingRecordIds.push(record.id)
      try {
        await this.request(`/api/records/${encodeURIComponent(record.id)}`, { method: 'DELETE' })
        this.history = this.history.filter(item => item.id !== record.id)
        if (this.currentBatchId === record.id) {
          this.previewUrl = ''
          this.results = []
          this.currentBatchId = ''
          this.currentTaskIds = []
          this.restoredTaskStyle = ''
          await this.loadHistory()
        }
        this.showMessage('记录已删除', 'success')
      } catch (error) {
        this.showMessage(error.message || '删除失败，请稍后重试', 'error')
      } finally {
        this.deletingRecordIds = this.deletingRecordIds.filter(id => id !== record.id)
      }
    },
    async removeHistoryGroup(record) {
      if (!record || !record.isGroup || this.deletingRecordIds.includes(record.id)) return
      try {
        await this.$confirm(
          `将删除本组 ${record.children.length} 条灯光转换记录，删除后无法恢复，是否继续？`,
          '删除整组记录',
          {
            confirmButtonText: '删除整组',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
      } catch (error) {
        return
      }

      this.deletingRecordIds.push(record.id)
      const childIds = record.children.map(child => child.id)
      try {
        const results = await Promise.allSettled(childIds.map(id => (
          this.request(`/api/records/${encodeURIComponent(id)}`, { method: 'DELETE' })
        )))
        const removedIds = childIds.filter((id, index) => results[index].status === 'fulfilled')
        this.history = this.history.filter(item => !removedIds.includes(item.id))
        if (removedIds.length === childIds.length) {
          this.showMessage(`已删除本组 ${removedIds.length} 条记录`, 'success')
        } else {
          this.showMessage(`已删除 ${removedIds.length} 条，另有 ${childIds.length - removedIds.length} 条删除失败`, 'warning')
          await this.loadHistory()
        }
      } finally {
        this.deletingRecordIds = this.deletingRecordIds.filter(id => id !== record.id)
      }
    },
    handleFileInput(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (file) this.acceptFile(file)
    },
    handleDrop(event) {
      this.dragging = false
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      if (file) this.acceptFile(file)
    },
    handleStyleReferenceInput(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (file) this.acceptStyleReference(file)
    },
    handleStyleReferenceDrop(event) {
      this.styleReferenceDragging = false
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      if (file) this.acceptStyleReference(file)
    },
    acceptStyleReference(file) {
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.styleReferenceError = '参考图格式不支持，请选择 PNG、JPG 或 WebP 图片。'
        this.showMessage(this.styleReferenceError, 'warning')
        return
      }
      if (file.size >= MAX_SOURCE_FILE_BYTES) {
        this.styleReferenceError = `参考图为 ${this.formatFileSize(file.size)}，超过 10MB 上限。`
        this.showMessage(this.styleReferenceError, 'warning')
        return
      }
      if (this.styleReferencePreviewUrl) URL.revokeObjectURL(this.styleReferencePreviewUrl)
      this.styleReferenceFile = file
      this.styleReferencePreviewUrl = URL.createObjectURL(file)
      this.styleReferenceError = ''
      this.results = []
      this.errorMessage = ''
      this.errorDetails = null
      this.showMessage('风格参考图已添加', 'success')
    },
    removeStyleReference() {
      if (this.generating) return
      if (this.styleReferencePreviewUrl) URL.revokeObjectURL(this.styleReferencePreviewUrl)
      this.styleReferenceFile = null
      this.styleReferencePreviewUrl = ''
      this.styleReferenceError = ''
      this.results = []
    },
    async acceptFile(file) {
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.fileValidationError = '文件格式不支持，请重新选择 PNG、JPG 或 WebP 图片。'
        this.showMessage(this.fileValidationError, 'warning')
        return
      }
      if (file.size >= MAX_SOURCE_FILE_BYTES) {
        const actualSize = this.formatFileSize(file.size)
        this.fileValidationError = `这张图片为 ${actualSize}，超过 10MB 上限。请压缩图片或降低分辨率后重新选择。`
        this.showMessage(`图片过大：${actualSize}，最大支持 10MB`, 'warning')
        return
      }
      const inspectionSerial = ++this.sourceInspectionSerial
      const candidateUrl = URL.createObjectURL(file)
      this.ratioDetecting = true
      let dimensions
      try {
        dimensions = await this.inspectImageDimensions(candidateUrl)
      } catch (error) {
        URL.revokeObjectURL(candidateUrl)
        if (inspectionSerial !== this.sourceInspectionSerial) return
        this.ratioDetecting = false
        this.fileValidationError = '无法识别图片尺寸，请重新上传有效的 PNG、JPG 或 WebP 图片。'
        this.showMessage(this.fileValidationError, 'error')
        return
      }
      if (inspectionSerial !== this.sourceInspectionSerial) {
        URL.revokeObjectURL(candidateUrl)
        return
      }

      const closestRatio = this.closestSupportedRatio(dimensions.width, dimensions.height)
      const isStandard169 = isAspectRatioWithinTolerance(dimensions.width, dimensions.height)
      if (this.lightingMode && !isStandard169) {
        const targetDimensions = outputDimensions(this.selectedResolution)
        const recommendedDimensions = `${targetDimensions.width}×${targetDimensions.height}`
        try {
          await this.$confirm(
            `检测到你上传的原图尺寸为 ${dimensions.width}×${dimensions.height}（约 ${closestRatio}），不符合灯光功能建议的标准画幅。当前选择 ${this.selectedResolution} 输出，建议使用 16:9、${recommendedDimensions} 像素的标准原图；其中 2K 标准尺寸为 2048×1152，4K 标准尺寸为 3840×2160。继续生成可能导致画面边缘、空间结构、镜头透视或物体位置出现偏差，也可能降低原图与结果图重叠对比的准确度。`,
            '原图尺寸与建议规格不一致',
            {
              confirmButtonText: '继续生成',
              cancelButtonText: '去裁切',
              type: 'warning',
              distinguishCancelAndClose: true,
              closeOnClickModal: false,
              closeOnPressEscape: true,
              customClass: 'source-ratio-confirm'
            }
          )
        } catch (action) {
          URL.revokeObjectURL(candidateUrl)
          if (inspectionSerial === this.sourceInspectionSerial) this.ratioDetecting = false
          if (action === 'cancel') {
            this.showMessage('已取消使用非标准尺寸图片，正在前往原图裁切', 'info')
            this.goToSourceCrop()
          }
          return
        }
      }
      if (inspectionSerial !== this.sourceInspectionSerial) {
        URL.revokeObjectURL(candidateUrl)
        return
      }
      this.commitSourceFile(file, candidateUrl, dimensions.width, dimensions.height, closestRatio, isStandard169)
    },
    commitSourceFile(file, candidateUrl, width, height, closestRatio, isStandard169) {
      this.fileValidationError = ''
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.sourceFile = file
      this.previewUrl = candidateUrl
      this.lightingZones = []
      this.activeLightingZoneId = ''
      this.zoneDrawingEnabled = this.lightingAdjustment
      this.nextLightingZoneId = 1
      this.sourceWidth = width
      this.sourceHeight = height
      this.selectedRatio = closestRatio
      this.sourceRatioWarning = isStandard169
        ? ''
        : `当前图片尺寸为 ${width}×${height}（约 ${closestRatio}）。建议标准尺寸：2K 2048×1152，4K 3840×2160。你已确认继续使用非标准画幅；生成时将保持原图尺寸，但空间结构、镜头透视和画面构图的还原精度可能受到影响。`
      this.ratioDetecting = false
      this.restoredTaskStyle = ''
      this.results = []
      this.resultLabels = []
      this.currentTaskLabels = []
      this.errorMessage = ''
      this.errorDetails = null
      this.currentBatchId = ''
      this.currentTaskIds = []
      this.showMessage(
        isStandard169
          ? `已识别标准 16:9 原图 ${width}×${height}`
          : `已确认继续使用 ${width}×${height}（约 ${closestRatio}）原图`,
        isStandard169 ? 'success' : 'warning'
      )
    },
    inspectImageDimensions(previewUrl) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
        image.onerror = () => reject(new Error('INVALID_IMAGE_DIMENSIONS'))
        image.src = previewUrl
      })
    },
    formatFileSize(bytes) {
      const megabytes = Number(bytes || 0) / (1024 * 1024)
      if (megabytes >= 1) return `${Number(megabytes.toFixed(2))}MB`
      return `${Math.max(1, Math.ceil(Number(bytes || 0) / 1024))}KB`
    },
    goToSourceCrop() {
      if (this.$route && this.$route.name === 'aspectRatioExpansion') return
      this.$router.push({ name: 'aspectRatioExpansion' })
    },
    closestSupportedRatio(width, height) {
      const sourceRatio = width / height
      return SUPPORTED_RATIOS.reduce((closest, ratio) => {
        const [ratioWidth, ratioHeight] = ratio.split(':').map(Number)
        const distance = Math.abs(Math.log(sourceRatio / (ratioWidth / ratioHeight)))
        return distance < closest.distance ? { ratio, distance } : closest
      }, { ratio: '16:9', distance: Number.POSITIVE_INFINITY }).ratio
    },
    async uploadFile(file) {
      const form = new FormData()
      form.append('file', file)
      try {
        return await this.request('/api/images/upload', {
          method: 'POST',
          body: form,
          timeoutMs: 180000,
          timeoutMessage: '原图上传超时，请关闭浏览器网络限速后重试',
          // Uploading the source image does not create a generation task or
          // consume points, so retrying a transient network failure is safe.
          retries: 2,
          retryDelayMs: 1200
        })
      } catch (error) {
        if (Number(error && error.status) === 413 || (error && error.code) === 'IMAGE_TOO_LARGE') {
          this.fileValidationError = `这张图片为 ${this.formatFileSize(file && file.size)}，服务器最大支持 10MB。请压缩图片或降低分辨率后重新选择。`
        }
        throw error
      }
    },
    async uploadSource() {
      return this.uploadFile(this.sourceFile)
    },
    buildLightingAdjustmentMaskFile() {
      if (!this.lightingZones.length || !this.sourceWidth || !this.sourceHeight) {
        return Promise.reject(new Error('无法生成灯光柔边遮罩，请重新框选灯具'))
      }
      const canvas = document.createElement('canvas')
      canvas.width = this.sourceWidth
      canvas.height = this.sourceHeight
      const context = canvas.getContext('2d')
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.globalCompositeOperation = 'destination-out'

      this.lightingZones.forEach(zone => {
        const zoneWidth = Math.max(2, (zone.width / 100) * canvas.width)
        const zoneHeight = Math.max(2, (zone.height / 100) * canvas.height)
        const centerX = ((zone.x + zone.width / 2) / 100) * canvas.width
        const centerY = ((zone.y + zone.height / 2) / 100) * canvas.height
        // 选框只定位灯具。遮罩至少向外扩展一小段画面，让灯芯、光晕和邻近受光
        // 在同一个椭圆柔边内过渡，避免把 UI 矩形变成结果中的亮暗补丁。
        const radiusX = Math.max(zoneWidth * 1.35, canvas.width * 0.035)
        const radiusY = Math.max(zoneHeight * 1.65, canvas.height * 0.055)
        context.save()
        context.translate(centerX, centerY)
        context.scale(radiusX, radiusY)
        const gradient = context.createRadialGradient(0, 0, 0, 0, 0, 1)
        gradient.addColorStop(0, 'rgba(0,0,0,1)')
        gradient.addColorStop(0.52, 'rgba(0,0,0,1)')
        gradient.addColorStop(0.78, 'rgba(0,0,0,.48)')
        gradient.addColorStop(1, 'rgba(0,0,0,0)')
        context.fillStyle = gradient
        context.beginPath()
        context.arc(0, 0, 1, 0, Math.PI * 2)
        context.fill()
        context.restore()
      })
      context.globalCompositeOperation = 'source-over'

      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (!blob) {
            reject(new Error('灯光柔边遮罩生成失败，请重新框选'))
            return
          }
          resolve(new File([blob], `lighting-soft-mask-${Date.now()}.png`, { type: 'image/png' }))
        }, 'image/png')
      })
    },
    async writeLightingPrompt() {
      if (this.writingLightingPrompt || this.lightingPromptCooldown || this.generating) return
      if (!this.sourceFile) {
        this.showMessage('请先上传一张新的原有效果图', 'warning')
        return
      }
      if (!this.lightingZones.length) {
        this.showMessage('请先框选至少一个灯光区域', 'warning')
        this.zoneDrawingEnabled = true
        return
      }
      if (!this.apiConfigured) {
        this.showMessage('请先配置 ToAPIs API Key', 'warning')
        return
      }

      this.writingLightingPrompt = true
      const previousPrompt = this.requirement
      try {
        const asset = await this.uploadFile(this.sourceFile)
        const result = await this.request('/api/prompts/write-lighting-adjustment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: [asset.url].filter(Boolean),
            floorPlanImageUrls: [],
            layoutNote: previousPrompt.trim(),
            viewpoint: this.lightingZoneInstructions,
            space: '室内效果图',
            style: '分区灯光控制'
          }),
          timeoutMs: 120000,
          timeoutMessage: 'AI 写作等待超时，请稍后重试'
        })
        const writtenPrompt = String(result.prompt || '').trim()
        if (!writtenPrompt) throw new Error('AI 未返回可用的灯光提示词')
        this.requirement = writtenPrompt.slice(0, 300)
        this.resetLightingAdjustmentResult()
        this.showMessage('AI 已结合原图和框选区域完成提示词', 'success')
      } catch (error) {
        this.requirement = previousPrompt
        this.showMessage(error.message || 'AI 写作失败，请稍后重试', 'error')
      } finally {
        this.writingLightingPrompt = false
        this.lightingPromptCooldown = true
        if (this.lightingPromptCooldownTimer) window.clearTimeout(this.lightingPromptCooldownTimer)
        this.lightingPromptCooldownTimer = window.setTimeout(() => {
          this.lightingPromptCooldown = false
          this.lightingPromptCooldownTimer = null
        }, 1200)
      }
    },
    createRequestId() {
      const randomPart = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`
      const prefix = this.lightingAdjustment ? 'lighting-adjustment' : this.lightingMode ? 'lighting-conversion' : 'style-replace'
      return `${prefix}-${randomPart}`.slice(0, 64)
    },
    normalizeError(error, fallbackMessage = '') {
      const source = error && typeof error === 'object' ? error : {}
      const pending = Boolean(source.pending)
      return {
        code: source.code || 'GENERATION_FAILED',
        message: (typeof error === 'string' ? error : source.message) || fallbackMessage || `${this.featureName}未能完成`,
        suggestion: source.suggestion || (pending
          ? '系统会自动确认任务编号；请稍后刷新任务状态，无需重新上传或重复提交。'
          : '请检查原图和补充要求后重新尝试；若仍失败，请联系管理员并提供任务编号。'),
        retryable: source.retryable !== false,
        pending
      }
    },
    setGenerationError(error) {
      this.errorDetails = this.normalizeError(error)
      this.errorMessage = this.errorDetails.message
    },
    async generateSelectedLightingEffects() {
      const effects = this.selectedLightingEffectItems.slice()
      if (!effects.length) {
        this.showMessage('请至少勾选一种灯光效果', 'warning')
        return
      }

      this.generating = true
      this.results = []
      this.resultLabels = effects.map(effect => effect.name)
      this.currentTaskLabels = []
      this.errorMessage = ''
      this.errorDetails = null
      this.currentBatchId = ''
      this.currentTaskIds = []
      this.progress = 4
      this.generationText = '正在同步实际执行提示词'

      try {
        await this.refreshLightingPromptPreviews(true)
        this.generationText = '正在上传原图'
        const asset = await this.uploadSource()
        this.progress = 12
        this.generationText = `正在创建 ${effects.length} 个独立灯光任务`
        const submittedAt = new Date().toISOString()
        const randomGroupPart = window.crypto && typeof window.crypto.randomUUID === 'function'
          ? window.crypto.randomUUID().replace(/-/g, '').slice(0, 14)
          : Math.random().toString(36).slice(2, 16)
        const groupEffectMask = effects.reduce((mask, effect) => {
          const index = this.lightingEffects.findIndex(item => item.id === effect.id)
          return index >= 0 ? mask | (1 << index) : mask
        }, 0)
        const groupRequestBase = effects.length > 1
          ? `lighting-batch-m${groupEffectMask.toString(16)}-${Date.now().toString(36)}-${randomGroupPart}`
          : ''
        const taskRequests = effects.map(effect => {
          const requestId = groupRequestBase
            ? `${groupRequestBase}--${effect.id}`
            : this.createRequestId()
          return { effect, requestId, pendingId: `interior-${requestId}` }
        })
        const optimisticRecords = taskRequests.map(({ effect, pendingId }) => ({
          id: pendingId,
          style: effect.name,
          size: this.selectedRatio,
          status: 'generating',
          prompt: this.buildLightingConversionPrompt(effect),
          errorMessage: '',
          imageModel: IMAGE_GENERATION_MODEL,
          taskIds: [],
          taskStates: [],
          expectedCount: 1,
          createdAt: submittedAt,
          updatedAt: submittedAt,
          source: asset.localUrl || this.previewUrl,
          result: ''
        }))
        const optimisticIds = new Set(optimisticRecords.map(record => record.id))
        this.history = [...optimisticRecords, ...this.history.filter(record => !optimisticIds.has(record.id))].slice(0, 18)
        this.historyTotal += optimisticRecords.length

        const submissions = await Promise.all(taskRequests.map(async ({ effect, requestId, pendingId }) => {
          try {
            const batch = await this.request('/api/generations', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                imageUrls: [asset.url],
                prompt: this.buildLightingConversionPrompt(effect),
                space: '原有效果图',
                style: effect.name,
                size: this.selectedRatio,
                resolution: this.selectedResolution,
                quality: this.selectedQuality,
                count: 1,
                stage: 'lighting_conversion',
                floorPlanRecordUrls: [],
                referenceRecordUrls: asset.localUrl ? [asset.localUrl] : [],
                requestId
              }),
              timeoutMs: 120000,
              retries: 1,
              timeoutMessage: `创建“${effect.name}”任务等待超时`
            })
            const batchId = batch.batchId || pendingId
            const submitted = await this.waitForSubmittedTasks(batchId, 1)
            const historyRecord = this.history.find(record => record.id === pendingId)
            if (historyRecord) {
              historyRecord.id = batchId
              historyRecord.taskIds = submitted.taskIds
              historyRecord.taskStates = submitted.tasks
              historyRecord.updatedAt = new Date().toISOString()
            }
            return { effect, batchId, taskId: submitted.taskIds[0] }
          } catch (error) {
            const historyRecord = this.history.find(record => record.id === pendingId)
            if (historyRecord) {
              historyRecord.status = error.pending ? 'generating' : 'failed'
              historyRecord.errorMessage = error.message || '任务提交失败'
              historyRecord.updatedAt = new Date().toISOString()
            }
            return { effect, error }
          }
        }))

        const successful = submissions.filter(item => item.taskId)
        const failed = submissions.filter(item => !item.taskId)
        if (!successful.length) {
          const firstError = failed[0] && failed[0].error
          throw firstError || new Error('未能创建灯光转换任务')
        }

        this.currentBatchId = successful[0].batchId
        this.currentTaskIds = successful.map(item => item.taskId)
        this.currentTaskLabels = successful.map(item => item.effect.name)
        this.resultLabels = [...this.currentTaskLabels]
        this.progress = 18
        this.generationText = `已创建 ${successful.length} 个任务，AI 正在分别处理`
        if (failed.length) {
          this.showMessage(`已创建 ${successful.length} 个任务，${failed.length} 个任务提交失败`, 'warning')
        } else {
          this.showMessage(`已成功创建 ${successful.length} 个独立灯光任务`, 'success')
        }

        this.results = await this.pollTasks(this.currentTaskIds, this.currentTaskLabels)
        this.resultLabels = [...this.currentTaskLabels]
        this.progress = 100
        this.generationText = '灯光转换已完成'
        this.showMessage(`${this.results.length} 种灯光效果已生成并保存`, 'success')
        await this.loadHistory()
      } catch (error) {
        this.setGenerationError(error)
        if (!error.pending) this.showMessage(this.errorDetails.message, 'error')
        await this.loadHistory()
      } finally {
        this.generating = false
      }
    },
    async generateReplacement() {
      if (this.generating) return
      if (!this.sourceFile) {
        this.showMessage('请先上传原有效果图', 'warning')
        return
      }
      if (this.ratioDetecting) {
        this.showMessage('正在识别原图画幅，请稍候', 'warning')
        return
      }
      if (!this.sourceWidth || !this.sourceHeight) {
        this.showMessage('未能识别原图尺寸，请重新上传图片', 'warning')
        return
      }
      if (this.lightingAdjustment && !this.lightingZones.length) {
        this.showMessage('请先在效果图上框选至少一个灯光区域', 'warning')
        this.zoneDrawingEnabled = true
        return
      }
      if (!this.lightingMode && !this.selectedStyle) {
        this.showMessage('请选择需要替换的新风格', 'warning')
        return
      }
      if (!this.apiConfigured) {
        this.showMessage('请先配置 ToAPIs API Key', 'warning')
        return
      }
      if (this.lightingMode && !this.lightingAdjustment) {
        await this.generateSelectedLightingEffects()
        return
      }
      this.generating = true
      this.results = []
      this.resultLabels = []
      this.currentTaskLabels = []
      this.errorMessage = ''
      this.errorDetails = null
      this.currentBatchId = ''
      this.currentTaskIds = []
      this.progress = 4
      this.generationText = '正在上传原图'
      const requestId = this.createRequestId()
      const pendingId = `interior-${requestId}`
      const submittedAt = new Date().toISOString()
      this.currentBatchId = pendingId
      const taskStage = this.taskStage
      const taskStyle = this.taskStyle
      const pendingRecord = {
        id: pendingId,
        style: taskStyle,
        size: this.selectedRatio,
        status: 'generating',
        error: '',
        taskIds: [],
        taskStates: [],
        expectedCount: Number(this.outputCount),
        createdAt: submittedAt,
        updatedAt: submittedAt,
        source: this.previewUrl,
        result: ''
      }
      savePendingGeneration({
        id: pendingId,
        space: '原有效果图',
        style: taskStyle,
        size: this.selectedRatio,
        prompt: this.finalPrompt,
        status: 'generating',
        stage: taskStage,
        progress: 0,
        expectedCount: Number(this.outputCount),
        tasks: [],
        images: [],
        inputContext: { floorPlanImages: [], referenceImages: [], annotation: null },
        createdAt: submittedAt,
        updatedAt: submittedAt
      })
      this.history = [pendingRecord, ...this.history.filter(item => item.id !== pendingRecord.id)].slice(0, 18)
      let generationRequestStarted = false
      try {
        const asset = await this.uploadSource()
        let lightingMaskAsset = null
        if (this.lightingAdjustment) {
          this.generationText = '正在构建柔边灯光影响区'
          lightingMaskAsset = await this.uploadFile(await this.buildLightingAdjustmentMaskFile())
        }
        this.progress = 12
        this.generationText = `正在创建${this.featureAction}任务`
        pendingRecord.source = asset.localUrl || this.previewUrl
        savePendingGeneration({
          id: pendingId,
          space: '原有效果图',
          style: taskStyle,
          size: this.selectedRatio,
          prompt: this.finalPrompt,
          status: 'generating',
          stage: taskStage,
          progress: 0,
          expectedCount: Number(this.outputCount),
          tasks: [],
          images: [],
          inputContext: {
            floorPlanImages: [],
            referenceImages: asset.localUrl ? [{ type: 'reference', label: '原始效果图', url: asset.localUrl }] : [],
            annotation: null
          },
          createdAt: submittedAt,
          updatedAt: submittedAt
        })
        generationRequestStarted = true
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            // 模型只接收干净原图和 Alpha 柔边遮罩，不接收带色框的 UI 定位图。
            // 百分比坐标仍保留在提示词中，用来说明每个灯具的开关状态。
            imageUrls: [asset.url],
            maskUrl: lightingMaskAsset ? lightingMaskAsset.url : null,
            prompt: this.finalPrompt,
            space: '原有效果图',
            style: taskStyle,
            size: this.selectedRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: Number(this.outputCount),
            stage: taskStage,
            // 灯光调节需要在服务端用干净原图保护结构和材质，仅合成受控的局部光照。
            // 这里单独记录原图，不能只依赖给模型看的区域引导图或提示词约束。
            floorPlanRecordUrls: this.lightingAdjustment && asset.localUrl ? [asset.localUrl] : [],
            referenceRecordUrls: asset.localUrl ? [asset.localUrl] : [],
            requestId
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: `创建${this.featureAction}任务等待超时，正在确认是否已成功提交`
        })
        this.currentBatchId = batch.batchId || pendingId
        pendingRecord.id = this.currentBatchId
        const submitted = await this.waitForSubmittedTasks(this.currentBatchId, Number(this.outputCount))
        const taskIds = submitted.taskIds
        this.currentTaskIds = taskIds
        this.showMessage(`已成功提交${this.featureAction}，正在生成中，请耐心等待`, 'success')
        pendingRecord.taskIds = taskIds
        pendingRecord.taskStates = submitted.tasks
        removePendingGeneration(pendingId)
        this.progress = 18
        this.generationText = this.lightingMode ? `FastIMG 执行${taskStyle}中` : 'FastIMG 重绘中'
        this.results = await this.pollTasks(taskIds)
        this.progress = 100
        this.generationText = `${this.featureName}已完成`
        this.showMessage(`${this.results.length} 张${taskStyle}方案已生成并保存`, 'success')
        this.loadHistory()
      } catch (error) {
        if (generationRequestStarted && !error.terminal && error.pending !== false) {
          error.pending = true
          error.retryable = true
          error.suggestion = error.suggestion || '任务可能已经提交成功，系统正在后台确认，请稍后刷新任务状态，无需重复提交。'
        }
        if (pendingRecord) {
          if (generationRequestStarted && error.retryable) {
            pendingRecord.status = 'generating'
            this.schedulePendingRecordRefresh()
          } else if (!pendingRecord.taskIds.length) {
            pendingRecord.status = 'failed'
            pendingRecord.error = error.message || '任务提交失败'
            removePendingGeneration(pendingRecord.id)
            this.currentBatchId = ''
            this.currentTaskIds = []
          }
        }
        this.setGenerationError(error)
        if (!error.pending) this.showMessage(this.errorDetails.message, 'error')
      } finally {
        this.generating = false
      }
    },
    async refreshPendingSubmission() {
      if (this.generating || !this.currentBatchId) return
      let shouldPoll = false
      this.generating = true
      this.generationText = '正在确认后台任务编号'
      this.progress = Math.max(this.progress, 16)
      try {
        const record = await this.request(
          `/api/records/${encodeURIComponent(this.currentBatchId)}`,
          { timeoutMs: 15000, retries: 2, timeoutMessage: '查询后台任务状态超时' }
        )
        if (record.status === 'failed') {
          const failedTask = (record.tasks || []).find(task => task && task.status === 'failed')
          const details = failedTask && failedTask.error ? failedTask.error : {}
          const error = new Error(details.message || record.errorMessage || `${this.featureName}任务提交失败`)
          error.code = details.code || 'GENERATION_FAILED'
          error.suggestion = details.suggestion || '请检查原图和补充要求后重新尝试。'
          error.retryable = details.retryable !== false
          throw error
        }
        const taskIds = (record.tasks || []).map(task => task && task.id).filter(Boolean)
        if (!taskIds.length) {
          const pending = new Error('任务已保存，AI 服务正在确认任务编号')
          pending.code = 'TASK_ID_PENDING'
          pending.suggestion = '系统会继续自动查询，请稍后再次刷新任务状态，无需重复提交。'
          pending.pending = true
          pending.retryable = true
          throw pending
        }
        this.currentTaskIds = taskIds
        this.errorMessage = ''
        this.errorDetails = null
        shouldPoll = true
        await this.loadHistory()
      } catch (error) {
        this.setGenerationError(error)
        if (error.pending) this.schedulePendingRecordRefresh()
        else await this.loadHistory()
      } finally {
        this.generating = false
      }
      if (shouldPoll) await this.continuePolling()
    },
    async pollTasks(taskIds, taskLabels = []) {
      const startedAt = Date.now()
      let consecutiveErrors = 0
      while (Date.now() - startedAt < 600000 && !this.pollingStopped) {
        let states = null
        try {
          states = await Promise.all(taskIds.map(id => this.request(`/api/generations/${encodeURIComponent(id)}`, {
            timeoutMs: 12000,
            retries: 1,
            timeoutMessage: '任务状态查询超时'
          })))
          consecutiveErrors = 0
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error
          consecutiveErrors += 1
          this.generationText = `网络波动，正在重新连接（${consecutiveErrors}/5）`
          if (consecutiveErrors >= 5) {
            const pendingError = new Error('连续多次无法查询生成进度，但任务可能仍在后台处理')
            pendingError.code = 'POLLING_UNAVAILABLE'
            pendingError.suggestion = '请检查网络后点击“继续查询”，无需重新提交任务。'
            pendingError.retryable = true
            pendingError.pending = true
            throw pendingError
          }
          await new Promise(resolve => window.setTimeout(resolve, 3000))
          continue
        }
        const images = states.map(item => item.status === 'completed' && item.result && item.result.data && item.result.data[0] ? item.result.data[0].url : '')
        const completedIndexes = images.reduce((indexes, image, index) => {
          if (image) indexes.push(index)
          return indexes
        }, [])
        this.results = completedIndexes.map(index => images[index])
        if (taskLabels.length === taskIds.length) {
          this.resultLabels = completedIndexes.map(index => taskLabels[index])
        }
        const average = states.reduce((sum, item) => sum + (item.status === 'completed' ? 100 : Number(item.progress || 0)), 0) / states.length
        this.progress = Math.max(18, Math.min(99, Math.round(18 + average * 0.81)))
        this.generationText = this.results.length ? `已完成 ${this.results.length}/${taskIds.length} 张` : `FastIMG ${this.lightingMode ? `执行${this.taskStyle}` : '重绘'}中 ${Math.round(average)}%`
        const activeHistoryRecord = this.history.find(item => item.id === this.currentBatchId)
        if (activeHistoryRecord) {
          activeHistoryRecord.status = states.every(item => item.status === 'completed') ? 'completed' : 'generating'
          activeHistoryRecord.taskStates = states
          activeHistoryRecord.result = this.results[0] || activeHistoryRecord.result
        }
        if (states.every(item => ['completed', 'failed'].includes(item.status))) {
          const failed = states.find(item => item.status === 'failed')
          if (failed) {
            const failedError = new Error(failed.error && failed.error.message ? failed.error.message : `${this.featureName}任务失败`)
            Object.assign(failedError, failed.error || {}, { terminal: true })
            throw failedError
          }
          if (images.some(image => !image)) {
            const emptyResultError = new Error('任务已完成，但 AI 服务未返回可用图片')
            emptyResultError.code = 'EMPTY_GENERATION_RESULT'
            emptyResultError.suggestion = '任务记录已保留，请联系管理员并提供任务编号。'
            emptyResultError.retryable = false
            emptyResultError.terminal = true
            throw emptyResultError
          }
          if (taskLabels.length === taskIds.length) this.resultLabels = [...taskLabels]
          return images
        }
        await new Promise(resolve => window.setTimeout(resolve, 3000))
      }
      const pendingError = new Error('生成时间较长，任务仍在后台处理中')
      pendingError.code = 'TASK_STILL_RUNNING'
      pendingError.suggestion = '请稍后点击“继续查询”，无需重新上传或重复创建任务。'
      pendingError.retryable = true
      pendingError.pending = true
      throw pendingError
    },
    async continuePolling(silent = false) {
      if (this.generating || !this.currentTaskIds.length) return
      this.generating = true
      this.errorMessage = ''
      this.errorDetails = null
      this.progress = Math.max(this.progress, 18)
      this.generationText = '正在恢复任务进度'
      try {
        this.results = await this.pollTasks(this.currentTaskIds, this.currentTaskLabels)
        this.progress = 100
        this.generationText = `${this.featureName}已完成`
        if (!silent) this.showMessage(`${this.featureName}任务已完成`, 'success')
        await this.loadHistory()
      } catch (error) {
        this.setGenerationError(error)
        if (!silent && !error.pending) this.showMessage(this.errorDetails.message, 'error')
      } finally {
        this.generating = false
      }
    },
    async copyPrompt(prompt = this.displayedFinalPrompt) {
      try {
        await navigator.clipboard.writeText(prompt)
        this.showMessage('完整提示词已复制', 'success')
      } catch (error) {
        this.showMessage('复制失败，请手动选择提示词', 'warning')
      }
    },
    statusText(status) {
      return { completed: '已完成', generating: '生成中', pending: '排队中', failed: '生成失败' }[status] || '处理中'
    },
    historyFailureReason(record) {
      if (!record) return '未返回具体失败原因，请刷新记录或稍后重试'
      const failedTask = (record.taskStates || []).find(task => task && task.status === 'failed')
      const taskError = failedTask && failedTask.error
      const reason = (taskError && (taskError.message || taskError.error))
        || record.errorMessage
        || record.error
      return String(reason || '未返回具体失败原因，请刷新记录或稍后重试').trim()
    },
    historyFailureCode(record) {
      if (!record) return ''
      const failedTask = (record.taskStates || []).find(task => task && task.status === 'failed')
      return failedTask && failedTask.error && failedTask.error.code
        ? String(failedTask.error.code).trim()
        : ''
    },
    formatRecordTime(value) {
      if (!value) return '时间未知'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '时间未知'
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date)
    },
    historyViewerIndex(recordId, type) {
      return this.historyViewerItems.findIndex(item => item.key === `${recordId}-${type}`)
    },
    previewImage(image, title, items = [], index = null) {
      if (!image) return
      this.$emit('preview-image', image, { title, items, index })
    },
    async downloadImage(image, index) {
      try {
        const filename = `${this.lightingMode ? this.taskStyle : `${this.selectedStyle}-风格替换`}-${index + 1}`
        const fastDownloadTaskId = this.lightingAdjustment ? this.currentTaskIds[index] : ''
        if (fastDownloadTaskId) {
          downloadGeneratedImageDirectly(fastDownloadTaskId, filename)
          this.showMessage('PNG 图片已开始高速下载', 'success')
          return
        }
        await downloadImageAsPng(
          image,
          filename
        )
        this.showMessage('PNG 图片已开始下载', 'success')
      } catch (error) {
        this.showMessage(error.message || '图片下载失败，请稍后重试', 'error')
      }
    },
    async downloadHistoryImage(record) {
      if (!record || !record.result) return
      try {
        const filename = `${record.style || this.featureName}-${this.formatRecordTime(record.createdAt)}`
        const fastDownloadTaskId = this.lightingAdjustment && Array.isArray(record.taskIds)
          ? record.taskIds.find(Boolean)
          : ''
        if (fastDownloadTaskId) {
          downloadGeneratedImageDirectly(fastDownloadTaskId, filename)
          this.showMessage('历史结果已开始高速下载', 'success')
          return
        }
        await downloadImageAsPng(this.historyImageDownloadUrl(record), filename)
        this.showMessage('历史结果已按 PNG 格式下载', 'success')
      } catch (error) {
        this.showMessage(error.message || '图片下载失败，请稍后重试', 'error')
      }
    },
    async downloadHistoryGroup(record) {
      if (!record || !record.isGroup || this.downloadingGroupIds.includes(record.id)) return
      const completed = record.children.filter(child => child.result)
      if (!completed.length) {
        this.showMessage('本组暂时没有可下载的完成结果', 'warning')
        return
      }

      this.downloadingGroupIds.push(record.id)
      try {
        const { default: JSZip } = await import('jszip')
        const downloads = await Promise.allSettled(completed.map(async (child, index) => {
          const blob = await getOriginalImageBlob(this.historyImageDownloadUrl(child), { timeoutMs: 60000 })
          const safeStyle = String(child.style || `灯光效果-${index + 1}`)
            .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
            .trim()
          return {
            filename: `${String(index + 1).padStart(2, '0')}-${safeStyle || `灯光效果-${index + 1}`}`,
            blob
          }
        }))
        const available = downloads.filter(item => item.status === 'fulfilled').map(item => item.value)
        if (!available.length) throw new Error('本组图片下载失败，请稍后重试')

        const archive = new JSZip()
        available.forEach(item => {
          archive.file(originalImageFilename(item.blob, item.filename), item.blob)
        })
        const zipBlob = await archive.generateAsync({
          type: 'blob',
          compression: 'STORE'
        })
        const objectUrl = URL.createObjectURL(zipBlob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = `灯光转换系列-${Date.now()}.zip`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2000)

        const failedCount = downloads.length - available.length
        this.showMessage(
          failedCount
            ? `已打包 ${available.length} 张，另有 ${failedCount} 张读取失败`
            : `本组 ${available.length} 张 OSS 原图已打包下载`,
          failedCount ? 'warning' : 'success'
        )
      } catch (error) {
        this.showMessage(error.message || '整组下载失败，请稍后重试', 'error')
      } finally {
        this.downloadingGroupIds = this.downloadingGroupIds.filter(id => id !== record.id)
      }
    },
    historyImageDownloadUrl(record) {
      const taskId = record && Array.isArray(record.taskIds) ? record.taskIds.find(Boolean) : ''
      return taskId
        ? `/api/generated-images/${encodeURIComponent(taskId)}/content`
        : (record && record.result ? record.result : '')
    },
    showMessage(message, type = 'info') {
      this.$message({ message, type, showClose: true, duration: 2600, offset: 24 })
    }
  }
}
</script>

<style scoped>
.style-replace-workspace { width: calc(100% - 68px); max-width: none; margin: 26px auto 64px; color: #202b25; }
.replace-hero { min-height: 94px; display: flex; align-items: flex-start; justify-content: space-between; gap: 28px; margin-bottom: 18px; }
.replace-kicker { color: #2563eb; font-size: 9px; font-weight: 800; letter-spacing: 1.8px; }
.replace-hero h2, .result-header h2, .history-section h2 { margin: 7px 0 6px; font-size: 22px; letter-spacing: -.5px; }
.replace-hero p, .result-header p { max-width: 760px; margin: 0; color: #7a857e; font-size: 11px; line-height: 1.7; }
.api-state { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 7px; border: 1px solid #e0e5e1; border-radius: 5px; padding: 8px 11px; background: #fff; color: #89928c; font-size: 9px; font-weight: 700; }
.api-state i { width: 7px; height: 7px; border-radius: 50%; background: #c6ccc8; }
.api-state.ready { border-color: #b8ddc4; color: #25834a; background: #f3fbf6; }.api-state.ready i { background: #31a55e; box-shadow: 0 0 0 4px rgba(49,165,94,.1); }
.replace-flow { display: grid; grid-template-columns: minmax(360px,.82fr) minmax(520px,1.18fr); gap: 16px; }
.flow-panel { min-width: 0; border: 1px solid #e2e7e3; border-radius: 8px; padding: 20px; background: #fff; box-shadow: 0 10px 28px rgba(37,51,43,.04); }
.flow-title { display: flex; align-items: center; gap: 11px; margin-bottom: 16px; }.flow-title > span { width: 29px; height: 29px; display: grid; place-items: center; flex: 0 0 29px; border-radius: 5px; background: #edf4ff; color: #2563eb; font-size: 10px; font-weight: 800; }.flow-title div { display: flex; flex-direction: column; gap: 3px; }.flow-title strong { font-size: 13px; }.flow-title small { color: #8b948e; font-size: 9px; }
.effect-dropzone { height: 330px; position: relative; display: grid; place-items: center; overflow: hidden; border: 1px dashed #bdc8c0; border-radius: 6px; background: #f7f9f7; cursor: pointer; transition: .2s; }
.effect-dropzone.filled { height: auto; min-height: 0; display: block; background: #edf0ed; }
.effect-dropzone:hover,.effect-dropzone.dragging { border-color: #2563eb; background: #f1f6ff; }
.effect-dropzone.invalid { border-color: #dc2626; background: #fff7f7; }
.effect-dropzone input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.effect-dropzone img { width: 100%; height: auto; display: block; object-fit: contain; background: #edf0ed; }
.dropzone-copy { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #7d8881; text-align: center; }.dropzone-copy > i { width: 48px; height: 48px; display: grid; place-items: center; border-radius: 50%; background: #edf4ff; color: #2563eb; font-size: 22px; }.dropzone-copy strong { color: #455149; font-size: 12px; }.dropzone-copy small { font-size: 9px; }.dropzone-guides { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 7px; margin-top: 3px; }.dropzone-guides em { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #d8e3f6; border-radius: 999px; padding: 6px 9px; background: #f5f8fe; color: #526b94; font-size: 9px; font-style: normal; font-weight: 700; }.dropzone-guides i { color: #2563eb; }.replace-image-action { position: absolute; right: 10px; bottom: 10px; border-radius: 4px; padding: 7px 10px; background: rgba(22,31,26,.82); color: #fff; font-size: 9px; }
.file-validation-error { display: flex; align-items: flex-start; gap: 9px; margin-top: 10px; border: 1px solid #fecaca; border-radius: 6px; padding: 10px 12px; background: #fff7f7; color: #991b1b; }
.file-validation-error > i { margin-top: 1px; font-size: 15px; }
.file-validation-error > div { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.file-validation-error strong { font-size: 11px; line-height: 1.4; }
.file-validation-error span { font-size: 10px; line-height: 1.6; }
.visually-hidden-file { width: 1px; height: 1px; position: absolute; overflow: hidden; opacity: 0; pointer-events: none; }
.lock-rules-caption { display: flex; align-items: center; gap: 6px; margin: 10px 0 0; color: #66736b; font-size: 9px; font-weight: 700; }.lock-rules-caption i { color: #2563eb; }.lock-rules { display: grid; grid-template-columns: repeat(3,1fr); gap: 7px; margin-top: 7px; }.lock-rules span { display: flex; align-items: center; justify-content: center; gap: 5px; border: 1px solid #e2e8e4; border-radius: 4px; padding: 8px 4px; background: #f4f6f4; color: #68736c; font-size: 8px; }.lock-rules i { color: #2563eb; }
.ratio-preflight-warning { display: grid; grid-template-columns: auto minmax(0,1fr) auto; align-items: center; gap: 11px; margin-top: 10px; border: 1px solid #e2aa00; border-left-width: 4px; border-radius: 6px; padding: 12px; background: #fff1ae; color: #704500; box-shadow: 0 5px 16px rgba(197,135,0,.1); }
.ratio-preflight-warning > i { width: 32px; height: 32px; align-self: flex-start; display: grid; place-items: center; border-radius: 50%; background: #ffd448; color: #704500; font-size: 17px; }
.ratio-preflight-warning > div { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.ratio-preflight-warning strong { color: #674000; font-size: 11px; line-height: 1.45; }
.ratio-preflight-warning span { color: #795617; font-size: 9px; line-height: 1.6; }
.ratio-preflight-warning button { min-height: 44px; display: inline-flex; align-items: center; justify-content: center; gap: 5px; border: 1px solid #bd7900; border-radius: 5px; padding: 0 11px; background: #fffaf0; color: #754a00; font-size: 9px; font-weight: 800; white-space: nowrap; cursor: pointer; }
.ratio-preflight-warning button:hover { background: #bd7900; color: #fff; }
.ratio-preflight-warning button:focus-visible { outline: 3px solid rgba(189,121,0,.28); outline-offset: 2px; }
.style-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; }.style-grid button { min-width: 0; height: 64px; position: relative; display: flex; align-items: center; gap: 9px; border: 1px solid #e1e6e2; border-radius: 6px; padding: 10px 28px 10px 11px; background: #fff; color: #4a564f; text-align: left; transition: border-color .16s ease, background .16s ease, box-shadow .16s ease, transform .16s ease; }.style-grid button:hover { border-color: #b6c6df; background: #fbfcfe; box-shadow: 0 4px 12px rgba(51,72,60,.06); transform: translateY(-1px); }.style-grid button.active { border-color: #7ca1e4; background: #f7f9fd; box-shadow: 0 0 0 1px rgba(37,99,235,.08); }.style-palette { width: 38px; flex: 0 0 38px; display: flex; align-items: center; }.style-palette i { width: 18px; height: 18px; flex: 0 0 18px; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 1px 4px rgba(38,48,42,.2); }.style-palette i + i { margin-left: -8px; }.style-copy { min-width: 0; display: flex; flex-direction: column; gap: 4px; }.style-grid strong { overflow: hidden; color: #354139; font-size: 10px; white-space: nowrap; text-overflow: ellipsis; }.style-grid small { overflow: hidden; color: #8b958f; font-size: 8px; white-space: nowrap; text-overflow: ellipsis; }.style-selected { width: 17px; height: 17px; position: absolute; top: 7px; right: 7px; display: grid; place-items: center; border-radius: 50%; background: #2563eb; color: #fff; box-shadow: 0 2px 5px rgba(37,99,235,.22); font-size: 8px; }.style-grid button.active strong { color: #1f55b5; }
.lighting-spec-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 9px; }.lighting-spec-grid > button { min-width: 0; min-height: 126px; position: relative; display: flex; align-items: flex-start; flex-direction: column; border: 1px solid #dce4ed; border-radius: 7px; padding: 13px; background: linear-gradient(145deg,#fbfdff,#f5f8fc); text-align: left; transition: border-color .18s ease, background .18s ease, box-shadow .18s ease, transform .18s ease; }.lighting-spec-grid > button:hover:not(:disabled) { border-color: #91aee0; background: #f7faff; box-shadow: 0 5px 15px rgba(37,99,235,.08); transform: translateY(-1px); }.lighting-spec-grid > button.active { border-color: #4f7fd5; background: linear-gradient(145deg,#f8fbff,#edf4ff); box-shadow: 0 0 0 2px rgba(37,99,235,.09); }.lighting-spec-grid > button:disabled { cursor: wait; }.lighting-spec-grid strong { margin: 9px 0 5px; color: #25364d; font-size: 10px; }.lighting-spec-grid > button.active strong { color: #1f55b5; }.lighting-spec-grid small { color: #758397; font-size: 8px; line-height: 1.55; }.lighting-icon { width: 30px; height: 30px; display: grid; place-items: center; border-radius: 50%; font-size: 14px; }.lighting-icon.brighten { background: #fff6cc; color: #b87800; }.lighting-icon.lights-off { background: #e8edf2; color: #536170; }.lighting-icon.night-bright { background: #fff2bd; color: #b45309; box-shadow: 0 0 16px rgba(217,119,6,.2); }.lighting-icon.night-medium { background: #e5e9ff; color: #4338ca; }.lighting-icon.night-low { background: #1e293b; color: #bfdbfe; box-shadow: inset 0 0 0 1px rgba(191,219,254,.22); }.lighting-icon.indoor { background: #fff0cf; color: #d98513; box-shadow: 0 0 18px rgba(236,163,39,.18); }.lighting-icon.black-line-art { background:#171717; color:#fff; box-shadow:inset 0 0 0 1px #3d3d3d; }.lighting-check { width: 18px; height: 18px; position: absolute; top: 10px; right: 10px; display: grid; place-items: center; border-radius: 50%; background: #2563eb; color: #fff; font-size: 9px; }
.lighting-spec-grid > button { min-height: 96px; padding: 10px 13px; }
.lighting-spec-grid strong { margin: 6px 0 3px; }
.lighting-icon.night-high-position { background: #dff5ff; color: #0369a1; box-shadow: 0 0 14px rgba(3,105,161,.14); }
.lighting-icon.night-low-position { background: #efe7ff; color: #6d28d9; box-shadow: inset 0 0 0 1px rgba(109,40,217,.1); }
.lighting-multi-toolbar { grid-column: 1 / -1; min-height: 44px; display: flex; align-items: center; justify-content: space-between; gap: 14px; border: 1px solid #dce5f3; border-radius: 7px; padding: 8px 10px 8px 12px; background: #f7faff; }.lighting-multi-toolbar > span { min-width: 0; display: flex; align-items: center; gap: 8px; }.lighting-multi-toolbar > span > i { color: #2563eb; font-size: 14px; }.lighting-multi-toolbar strong { margin: 0; color: #2457ad; font-size: 10px; white-space: nowrap; }.lighting-multi-toolbar small { overflow: hidden; color: #71839e; font-size: 8px; white-space: nowrap; text-overflow: ellipsis; }.lighting-multi-toolbar button { min-width: 86px; min-height: 32px; border: 1px solid #8eace0; border-radius: 5px; padding: 6px 12px; background: #fff; color: #2457ad; font-size: 9px; font-weight: 800; cursor: pointer; }.lighting-multi-toolbar button:hover:not(:disabled) { border-color: #2563eb; background: #edf4ff; }.lighting-multi-toolbar button:disabled { cursor: wait; opacity: .6; }
.simple-lighting-control { display: flex; flex-direction: column; justify-content: center; min-height: 310px; border: 1px solid #dce5f3; border-radius: 8px; padding: 34px 32px; background: linear-gradient(145deg,#fbfdff,#f5f8fc); }
.brightness-value { display: flex; align-items: center; justify-content: space-between; gap: 20px; }.brightness-value > span { display: flex; align-items: center; gap: 9px; color: #34434f; font-size: 13px; font-weight: 800; }.brightness-value > span i { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 50%; background: #fff0ce; color: #dc8c11; font-size: 16px; box-shadow: 0 0 22px rgba(220,140,17,.15); }.brightness-value label { display: flex; align-items: center; border: 1px solid #afc5ec; border-radius: 6px; overflow: hidden; background: #fff; }.brightness-value input { width: 72px; border: 0; padding: 10px 8px 10px 13px; outline: none; color: #1956be; font-size: 18px; font-weight: 800; text-align: right; }.brightness-value em { padding-right: 12px; color: #4f6f9f; font-size: 12px; font-style: normal; font-weight: 800; }
.master-brightness-slider { width: 100%; height: 7px; margin: 38px 0 12px; accent-color: #2563eb; cursor: pointer; }.master-brightness-slider:disabled { cursor: wait; }.brightness-scale { display: flex; justify-content: space-between; color: #89958e; font-size: 8px; }.simple-lighting-control > p { display: flex; align-items: flex-start; gap: 7px; margin: 32px 0 0; border-radius: 5px; padding: 10px 12px; background: #eef4fd; color: #58708e; font-size: 9px; line-height: 1.65; }.simple-lighting-control > p i { flex: 0 0 auto; margin-top: 2px; color: #2563eb; }
.zone-lighting-control { min-height: 310px; display: flex; flex-direction: column; border: 1px solid #dce5f3; border-radius: 8px; padding: 16px; background: linear-gradient(145deg,#fbfdff,#f5f8fc); }
.zone-control-summary { display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #e2e8f1; padding: 0 2px 13px; }
.zone-summary-icon { width: 34px; height: 34px; flex: 0 0 34px; display: grid; place-items: center; border-radius: 7px; background: #edf4ff; color: #2563eb; font-size: 15px; }
.zone-control-summary > div { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 4px; }
.zone-control-summary strong { color: #344256; font-size: 11px; }
.zone-control-summary small { color: #8591a1; font-size: 8px; }
.zone-count { width: 27px; height: 27px; flex: 0 0 27px; display: grid; place-items: center; border-radius: 50%; background: #2563eb; color: #fff; font-size: 10px; font-weight: 800; }
.zone-list { max-height: 188px; overflow-y: auto; display: flex; flex-direction: column; gap: 7px; margin-top: 11px; padding-right: 3px; }
.zone-list article { min-height: 55px; display: flex; align-items: center; gap: 9px; border: 1px solid #dfe6ef; border-radius: 6px; padding: 7px 7px 7px 9px; background: #fff; cursor: pointer; transition: border-color .16s ease, box-shadow .16s ease; }
.zone-list article:hover,.zone-list article.active { border-color: #6f97df; background: #f5f8ff; box-shadow: inset 3px 0 0 #2563eb, 0 0 0 2px rgba(37,99,235,.08); }
.zone-number { width: 23px; height: 23px; flex: 0 0 23px; display: grid; place-items: center; border-radius: 50%; background: #eaf1fd; color: #275fbd; font-size: 9px; font-weight: 800; }
.zone-name { min-width: 82px; flex: 1; display: flex; flex-direction: column; gap: 3px; }
.zone-name strong { color: #405064; font-size: 9px; }
.zone-name small { color: #949daa; font-size: 7px; }
.zone-state-switch { flex: 0 0 auto; display: grid; grid-template-columns: 1fr 1fr; overflow: hidden; border: 1px solid #dce3ec; border-radius: 4px; background: #f4f6f9; }
.zone-state-switch button { min-width: 55px; height: 29px; display: inline-flex; align-items: center; justify-content: center; gap: 4px; border: 0; background: transparent; color: #798596; font-size: 8px; }
.zone-state-switch button.active:first-child { background: #eaf8ef; color: #21864b; }
.zone-state-switch button.active:last-child { background: #fff0f1; color: #c83a43; }
.zone-delete { width: 28px; height: 28px; flex: 0 0 28px; display: grid; place-items: center; border: 0; border-radius: 4px; background: transparent; color: #a0a8b3; font-size: 11px; }
.zone-delete:hover:not(:disabled) { background: #fff0f1; color: #cf3f48; }
.zone-empty-action { min-height: 116px; display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 12px; border: 1px dashed #b6c8e3; border-radius: 7px; background: #f7faff; color: #285fb9; text-align: left; }
.zone-empty-action:disabled { color: #9ca7b5; cursor: default; }
.zone-empty-action > i { width: 40px; height: 40px; display: grid; place-items: center; border-radius: 50%; background: #e9f1ff; font-size: 18px; }
.zone-empty-action > span { display: flex; flex-direction: column; gap: 5px; }
.zone-empty-action strong { font-size: 10px; }
.zone-empty-action small { color: #8996a8; font-size: 8px; font-weight: 400; }
.on-brightness-control { margin-top: 11px; border: 1px solid #e1e7ef; border-radius: 6px; padding: 10px 12px; background: #fff; }
.on-brightness-control > div:first-child { display: flex; align-items: center; justify-content: space-between; color: #526174; font-size: 9px; }
.on-brightness-control > div:first-child strong { color: #1f59bb; font-size: 11px; }
.on-brightness-control input { width: 100%; height: 5px; margin: 10px 0 7px; accent-color: #e2a72b; cursor: pointer; }
.on-brightness-control input:disabled { opacity: .45; cursor: default; }
.zone-prompt-input { display: flex; flex-direction: column; gap: 7px; margin-top: 11px; border: 1px solid #dce5f2; border-radius: 6px; padding: 11px 12px; background: #fff; }
.zone-prompt-title { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.zone-prompt-title strong { display: inline-flex; align-items: center; gap: 5px; color: #46566a; font-size: 9px; }
.zone-prompt-title strong i { color: #2563eb; font-size: 11px; }
.zone-prompt-actions { display: inline-flex; align-items: center; gap: 9px; }
.zone-prompt-actions button { min-height: 36px; display: inline-flex; align-items: center; justify-content: center; gap: 5px; border: 1px solid #a8c0ea; border-radius: 5px; padding: 0 11px; background: #edf4ff; color: #245ebc; font-size: 9px; font-weight: 700; touch-action: manipulation; }
.zone-prompt-actions button:hover:not(:disabled) { border-color: #2563eb; background: #2563eb; color: #fff; }
.zone-prompt-actions button:disabled { opacity: .65; cursor: wait; }
.zone-prompt-actions button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.zone-prompt-actions em { color: #98a1ad; font-size: 8px; font-style: normal; }
.zone-prompt-input textarea { width: 100%; height: 72px; resize: vertical; border: 1px solid #dfe5ed; border-radius: 5px; padding: 9px 10px; outline: none; background: #fbfcfe; color: #344256; font: 9px/1.65 Manrope, "PingFang SC", "Microsoft YaHei", sans-serif; }
.zone-prompt-input textarea:focus { border-color: #6d98df; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,.08); }
.zone-prompt-input textarea:disabled { background: #f3f5f7; cursor: wait; }
.zone-prompt-input > small { color: #8995a5; font-size: 7px; line-height: 1.55; }
.zone-control-note { display: flex; align-items: flex-start; gap: 6px; margin: auto 0 0; padding-top: 11px; color: #708095; font-size: 8px; line-height: 1.55; }
.zone-control-note i { flex: 0 0 auto; margin-top: 1px; color: #2563eb; }
.style-reference-upload { margin-top: 16px; }
.style-reference-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 8px; }
.style-reference-heading > span { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 7px; color: #46534b; }
.style-reference-heading strong { font-size: 10px; }
.style-reference-heading em { border-radius: 999px; padding: 3px 7px; background: #eef2ef; color: #77837b; font-size: 8px; font-style: normal; font-weight: 700; line-height: 1; }
.style-reference-heading small { display: inline-flex; align-items: flex-start; justify-content: flex-end; gap: 5px; color: #7d8981; font-size: 8px; line-height: 1.5; text-align: right; }
.style-reference-heading small i { flex: 0 0 auto; margin-top: 1px; color: #6b8ac4; }
.style-reference-dropzone { min-height: 86px; position: relative; display: grid; place-items: center; overflow: hidden; border: 1px dashed #b9c7bd; border-radius: 7px; background: #f8faf8; color: #647169; cursor: pointer; transition: border-color .18s ease, background .18s ease, box-shadow .18s ease; }
.style-reference-dropzone:hover,.style-reference-dropzone.dragging { border-color: #2563eb; background: #f3f7ff; }
.style-reference-dropzone:focus-within { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.style-reference-dropzone.invalid { border-color: #dc5a5a; background: #fff8f8; }
.style-reference-file-input { width: 100%; height: 100%; position: absolute; z-index: 2; inset: 0; margin: 0; opacity: 0; cursor: pointer; }
.style-reference-file-input:disabled { cursor: wait; }
.style-reference-empty { width: 100%; display: grid; grid-template-columns: 38px minmax(0,1fr) auto; align-items: center; gap: 11px; padding: 15px; }
.style-reference-empty > i { width: 38px; height: 38px; display: grid; place-items: center; border-radius: 50%; background: #eaf1ff; color: #2563eb; font-size: 17px; }
.style-reference-empty > span { min-width: 0; display: flex; flex-direction: column; gap: 4px; text-align: left; }
.style-reference-empty strong { color: #3d4a42; font-size: 10px; }
.style-reference-empty small { color: #8a958e; font-size: 8px; }
.style-reference-empty > b { min-height: 32px; display: inline-flex; align-items: center; justify-content: center; border: 1px solid #b8caed; border-radius: 5px; padding: 0 11px; background: #fff; color: #2559b2; font-size: 9px; white-space: nowrap; }
.style-reference-dropzone.filled { min-height: 150px; border-style: solid; background: #eef1ef; }
.style-reference-dropzone > img { width: 100%; height: 150px; display: block; object-fit: contain; background: #eef1ef; }
.style-reference-replace { position: absolute; z-index: 1; right: 10px; bottom: 10px; display: inline-flex; align-items: center; gap: 5px; border-radius: 5px; padding: 7px 10px; background: rgba(28,39,32,.84); color: #fff; font-size: 9px; line-height: 1; backdrop-filter: blur(6px); }
.style-reference-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 7px; }
.style-reference-meta > span { min-width: 0; display: inline-flex; align-items: center; gap: 5px; color: #4e775c; font-size: 8px; line-height: 1.5; }
.style-reference-meta > span i { flex: 0 0 auto; color: #2f9954; }
.style-reference-meta button { min-width: 44px; min-height: 32px; flex: 0 0 auto; border: 0; border-radius: 4px; background: transparent; color: #b04747; font-size: 8px; cursor: pointer; }
.style-reference-meta button:hover:not(:disabled) { background: #fff1f1; }
.style-reference-meta button:focus-visible { outline: 2px solid #dc5a5a; outline-offset: 2px; }
.style-reference-meta button:disabled { opacity: .55; cursor: wait; }
.style-reference-error { display: flex; align-items: flex-start; gap: 5px; margin: 7px 0 0; color: #b53e3e; font-size: 8px; line-height: 1.5; }
.style-reference-error i { flex: 0 0 auto; margin-top: 1px; }
.custom-requirement { display: block; margin-top: 14px; }.custom-requirement > span { display: flex; justify-content: space-between; margin-bottom: 7px; color: #46534b; font-size: 10px; font-weight: 700; }.custom-requirement em { color: #929b95; font-size: 8px; font-style: normal; font-weight: 500; }.custom-requirement textarea { width: 100%; height: 74px; resize: none; border: 1px solid #dfe5e0; border-radius: 5px; padding: 10px 11px; outline: none; color: #344139; font-size: 10px; line-height: 1.6; }.custom-requirement textarea:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.08); }
.prompt-preview-panel { margin-top: 16px; border: 1px solid #d7e2f5; border-radius: 8px; padding: 15px 16px; background: #f7faff; box-shadow: 0 8px 22px rgba(37,99,235,.04); }.prompt-preview-panel > header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 11px; }.prompt-preview-title { min-width: 0; display: flex; align-items: center; gap: 10px; }.prompt-preview-title > span { width: 29px; height: 29px; flex: 0 0 29px; display: grid; place-items: center; border-radius: 5px; background: #2563eb; color: #fff; font-size: 10px; font-weight: 800; }.prompt-preview-title > div { min-width: 0; display: flex; flex-direction: column; gap: 3px; }.prompt-preview-title strong { color: #25332b; font-size: 12px; }.prompt-preview-title small { color: #7f8a83; font-size: 9px; }.prompt-preview-panel > header button { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 5px; border: 1px solid #b8caed; border-radius: 5px; padding: 7px 10px; background: #fff; color: #2559b2; font-size: 9px; font-weight: 700; }.prompt-preview-panel > header button:hover { border-color: #2563eb; background: #edf4ff; }.prompt-preview-panel pre { max-height: 176px; margin: 0; overflow: auto; border: 1px solid #dfe7f5; border-radius: 5px; padding: 12px 13px; background: #fff; color: #46554c; font: 10px/1.7 Manrope, "PingFang SC", "Microsoft YaHei", sans-serif; white-space: pre-wrap; word-break: break-word; }
.lighting-task-prompts { display: grid; gap: 9px; }.lighting-task-prompts article { overflow: hidden; border: 1px solid #d9e4f6; border-radius: 6px; background: #fff; }.lighting-task-prompts article > header { min-height: 38px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #e6ecf6; padding: 6px 8px 6px 12px; background: #f9fbff; }.lighting-task-prompts article > header strong { color: #28589f; font-size: 10px; }.lighting-task-prompts article > header span { color: #7b8da7; font-size: 8px; }.lighting-task-prompts article > header button { min-height: 28px; display: inline-flex; align-items: center; gap: 4px; margin-left: auto; border: 1px solid #b8caed; border-radius: 4px; padding: 5px 9px; background: #fff; color: #2559b2; font-size: 8px; font-weight: 700; cursor: pointer; }.lighting-task-prompts article > header button:hover { border-color: #2563eb; background: #edf4ff; }.lighting-task-prompts article pre { max-height: 128px; border: 0; border-radius: 0; }
.generation-bar { position: relative; display: grid; grid-template-columns: auto minmax(0,1fr) auto; align-items: center; gap: 22px; margin: 16px 0 28px; border: 1px solid #dce3de; border-radius: 8px; padding: 14px 16px; background: #fff; box-shadow: 0 9px 24px rgba(37,51,43,.05); }
.generation-intro { min-width: 150px; display: flex; align-items: center; gap: 10px; padding-right: 20px; border-right: 1px solid #e7ebe8; }.generation-intro > span { width: 34px; height: 34px; flex: 0 0 34px; display: grid; place-items: center; border-radius: 6px; background: #edf4ff; color: #2563eb; font-size: 15px; }.generation-intro > div { display: flex; flex-direction: column; gap: 3px; }.generation-intro strong { color: #2f3c34; font-size: 11px; }.generation-intro small { color: #909992; font-size: 8px; white-space: nowrap; }
.output-settings { min-width: 0; display: flex; align-items: center; gap: 20px; }.setting-group { display: flex; align-items: center; gap: 9px; }.setting-label { color: #7a857e; font-size: 9px; font-weight: 700; white-space: nowrap; }.fixed-output-count { border: 1px solid #cfd8d1; border-radius: 4px; padding: 7px 13px; background: #f8faf8; color: #35423a; font-size: 10px; white-space: nowrap; }.model-pill { min-width: 152px; display: flex; align-items: center; gap: 9px; border-left: 1px solid #e7ebe8; padding-left: 20px; color: #5d6861; }.model-pill > i { width: 9px; height: 9px; flex: 0 0 9px; border-radius: 50%; background: linear-gradient(135deg,#4285f4,#a855f7,#f59e0b); box-shadow: 0 0 0 4px #f1f4ff; }.model-pill > span { display: flex; flex-direction: column; gap: 2px; }.model-pill small { color: #939b96; font-size: 8px; }.model-pill strong { color: #4b5750; font-size: 9px; white-space: nowrap; }.generate-button { min-width: 230px; height: 54px; display: grid; grid-template-columns: 26px auto; grid-template-rows: 1fr 1fr; align-items: center; column-gap: 8px; border: 0; border-radius: 6px; padding: 9px 20px; background: #2563eb; color: #fff; box-shadow: 0 9px 20px rgba(37,99,235,.2); }.generate-button:hover:not(:disabled) { background: #1d4ed8; }.generate-button:disabled { opacity: .7; cursor: wait; }.generate-button > i,.button-spinner { grid-row: 1 / 3; justify-self: center; font-size: 18px; }.generate-button > span:not(.button-spinner) { align-self: end; font-size: 11px; font-weight: 800; text-align: left; }.generate-button small { align-self: start; color: rgba(255,255,255,.7); font-size: 8px; text-align: left; }.button-spinner { width: 17px; height: 17px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }.progress-track { position: absolute; left: 0; right: 0; bottom: -1px; height: 3px; overflow: hidden; border-radius: 0 0 8px 8px; background: #e8edf5; }.progress-track i { display: block; height: 100%; background: #2563eb; transition: width .4s ease; }
.auto-ratio-control { min-width: 190px; display: flex; align-items: center; gap: 8px; border: 1px solid #dfe5e0; border-radius: 5px; padding: 7px 9px; background: #f7f9f7; }.auto-ratio-control strong { flex: 0 0 auto; border-radius: 4px; padding: 4px 7px; background: #e9edf0; color: #59655e; font-size: 9px; }.auto-ratio-control span { color: #929b95; font-size: 8px; white-space: nowrap; }.auto-ratio-control.detected { border-color: #b8d8c1; background: #f3faf5; }.auto-ratio-control.detected strong { background: #dff2e5; color: #237444; }.auto-ratio-control.detected span { color: #4f7a5e; }
.result-section { border-top: 1px solid #dfe5e0; padding-top: 24px; }.result-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 14px; }.result-badge { display: inline-flex; align-items: center; gap: 6px; border-radius: 5px; padding: 8px 10px; background: #edf9f1; color: #26834a; font-size: 9px; font-weight: 700; }
.comparison-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 12px; }.image-card { min-width: 0; overflow: hidden; border: 1px solid #dfe5e0; border-radius: 7px; background: #fff; }.image-card > header { height: 48px; display: flex; align-items: center; gap: 9px; border-bottom: 1px solid #e7ebe8; padding: 0 12px; }.image-card header > span { border-radius: 3px; padding: 4px 6px; background: #eef1ee; color: #6e7972; font-size: 8px; font-weight: 800; }.result-card header > span { background: #edf4ff; color: #2563eb; }.image-card header strong { overflow: hidden; color: #445048; font-size: 10px; white-space: nowrap; text-overflow: ellipsis; }.image-card header button { width: 30px; height: 30px; margin-left: auto; border: 0; border-radius: 4px; background: #f1f4f1; color: #4f5a53; }.image-stage { height: 320px; display: grid; place-items: center; overflow: hidden; background: #edf0ed; }.image-stage img { width: auto; height: auto; max-width: 100%; max-height: 100%; display: block; object-fit: contain; object-position: center; }.image-stage img.zoomable-image { cursor: zoom-in; transition: filter .18s ease; }.image-stage img.zoomable-image:hover { filter: brightness(.96); }.image-empty { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 20px; color: #98a09b; text-align: center; }.image-empty > i { font-size: 27px; color: #b0b8b2; }.image-empty strong { color: #647069; font-size: 11px; }.image-empty span { font-size: 8px; line-height: 1.5; }.result-loader { display: flex; gap: 5px; }.result-loader i { width: 7px; height: 7px; border-radius: 50%; background: #2563eb; animation: pulse 1.1s ease-in-out infinite; }.result-loader i:nth-child(2) { animation-delay: .15s; }.result-loader i:nth-child(3) { animation-delay: .3s; }
.lighting-result-section .comparison-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }.lighting-result-section .source-card { grid-column: auto; }.lighting-result-section .image-stage.has-image { height: auto; display: block; overflow: visible; }.lighting-result-section .image-stage.has-image img { width: 100%; height: auto; max-width: none; max-height: none; }
.task-live-status { min-height:86px; position:relative; overflow:hidden; display:grid; grid-template-columns:42px minmax(0,1fr); align-items:center; gap:13px; margin-top:14px; border:1px solid #a9c5f5; border-radius:8px; padding:14px 16px 18px; background:linear-gradient(135deg,#f5f9ff,#edf4ff); color:#245ebc; box-shadow:0 8px 24px rgba(37,99,235,.08); }
.task-live-spinner { width:38px; height:38px; display:grid; place-items:center; border-radius:50%; background:#fff; box-shadow:0 4px 14px rgba(37,99,235,.14); }
.task-live-spinner i { width:20px; height:20px; border:3px solid #d8e5fb; border-top-color:#2563eb; border-radius:50%; animation:spin .85s linear infinite; }
.task-live-copy { min-width:0; display:flex; flex-direction:column; gap:4px; }
.task-live-copy strong { color:#174d9f; font-size:14px; }
.task-live-copy strong b { margin-left:5px; color:#2563eb; font-size:17px; }
.task-live-copy span { overflow:hidden; color:#3767ad; font-size:10px; font-weight:700; text-overflow:ellipsis; white-space:nowrap; }
.task-live-copy small { color:#7186a7; font-size:9px; line-height:1.5; }
.task-live-progress { height:5px; position:absolute; right:0; bottom:0; left:0; overflow:hidden; background:#dce8fa; }
.task-live-progress i { height:100%; display:block; border-radius:0 999px 999px 0; background:linear-gradient(90deg,#2563eb,#5b8def); box-shadow:0 0 10px rgba(37,99,235,.38); transition:width .45s ease; }
.generation-error { display: flex; align-items: center; gap: 11px; margin-top: 12px; border: 1px solid #efc4c4; border-radius: 6px; padding: 12px 14px; background: #fff5f5; color: #ad3b3b; }.generation-error > i { flex: 0 0 auto; font-size: 19px; }.generation-error > div { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 4px; overflow-wrap: anywhere; }.generation-error strong { font-size: 10px; }.generation-error span,.generation-error small { font-size: 9px; line-height: 1.5; }.generation-error small { color: #9b6565; }.generation-error button { min-width: 88px; min-height: 44px; flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid #dda9a9; border-radius: 5px; padding: 0 14px; background: #fff; color: #a53c3c; font-size: 10px; font-weight: 700; cursor: pointer; transition: border-color .16s ease, background .16s ease, box-shadow .16s ease; }.generation-error button:hover:not(:disabled) { border-color: #c96f6f; background: #fffafa; box-shadow: 0 4px 12px rgba(165,60,60,.1); }.generation-error button:focus-visible { outline: 3px solid rgba(197,65,65,.24); outline-offset: 2px; }.generation-error button:disabled { cursor: wait; opacity: .55; }
.history-section { margin-top: 32px; border-top: 1px solid #dfe5e0; padding-top: 26px; }.history-section > header { margin-bottom: 16px; }.history-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 16px; }.history-grid article { min-width: 0; overflow: hidden; border: 1px solid #dce3de; border-radius: 8px; background: #fff; box-shadow: 0 10px 28px rgba(37,51,43,.06); transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease; }.history-grid article:hover { border-color: #c8d3cb; box-shadow: 0 16px 36px rgba(37,51,43,.1); transform: translateY(-2px); }.history-images { width: 100%; display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 1px; padding: 1px; background: #dfe5e1; }.history-thumb { min-width: 0; position: relative; aspect-ratio: 9 / 16; overflow: hidden; border: 0; padding: 0; background: #eef1ef; cursor: zoom-in; }.history-thumb img { width: 100%; height: 100%; display: block; object-fit: contain; object-position: center; background: #eef1ef; transition: transform .22s ease, filter .22s ease; }.history-image-label { position: absolute; left: 10px; top: 10px; z-index: 1; display: inline-flex; align-items: center; border-radius: 4px; padding: 5px 8px; background: rgba(24,34,28,.76); color: #fff; font-size: 9px; font-weight: 700; line-height: 1; backdrop-filter: blur(6px); }.history-image-label.result-label { background: rgba(37,99,235,.88); }.history-thumb > i { position: absolute; right: 10px; bottom: 10px; width: 30px; height: 30px; display: grid; place-items: center; border-radius: 50%; background: rgba(18,27,22,.76); color: #fff; font-size: 13px; opacity: 0; transform: translateY(4px); transition: .18s ease; }.history-thumb:hover img { filter: brightness(.9); transform: scale(1.025); }.history-thumb:hover > i,.history-thumb:focus-visible > i { opacity: 1; transform: translateY(0); }.history-thumb:focus-visible { z-index: 1; outline: 3px solid #2563eb; outline-offset: -3px; }.history-images > span:not(.history-image-label) { min-height: 100%; display: grid; place-items: center; background: #f4f6f4; color: #7f8983; font-size: 10px; }.history-info { min-width: 0; min-height: 68px; display: flex; flex-direction: column; justify-content: center; gap: 6px; padding: 13px 96px 13px 15px; }.history-grid strong { overflow: hidden; color: #253129; font-size: 13px; white-space: nowrap; text-overflow: ellipsis; }.history-grid small { color: #87918b; font-size: 10px; }.history-download-button,.history-delete-button { width: 34px; height: 34px; position: absolute; bottom: 17px; display: grid; place-items: center; border: 1px solid #dce2de; border-radius: 5px; padding: 0; background: #fff; color: #7f8983; cursor: pointer; transition: .16s; }.history-download-button { right: 52px; color: #2563eb; }.history-delete-button { right: 12px; }.history-grid article { position: relative; }.history-download-button:hover { border-color: #9ab5e9; background: #f4f8ff; }.history-delete-button:hover:not(:disabled) { border-color: #e7b3b3; background: #fff5f5; color: #c43d3d; }.history-download-button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }.history-delete-button:focus-visible { outline: 2px solid #dc4949; outline-offset: 2px; }.history-delete-button:disabled { cursor: wait; opacity: .6; }
.history-grid .history-group-card { grid-column: 1 / -1; border-color: #cfdcf0; background: linear-gradient(180deg,#fbfdff 0,#fff 100%); }.history-group-header { min-height: 50px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #dce5f3; padding: 9px 15px; background: #f4f8ff; }.history-group-header > span { display: inline-flex; align-items: center; gap: 6px; border-radius: 4px; padding: 6px 8px; background: #2563eb; color: #fff; font-size: 9px; font-weight: 800; }.history-group-header > strong { color: #244c89; font-size: 13px; }.history-group-header > small { margin-left: auto; color: #627898; font-size: 9px; font-weight: 700; }.history-group-images { width: 100%; display: grid; gap: 1px; border-bottom: 1px solid #dfe5e1; padding: 1px; background: #dfe5e1; }.history-group-result { min-width: 0; position: relative; background: #eef1ef; }.history-group-images .history-thumb { width: 100%; height: auto; aspect-ratio: 16 / 10; }.history-group-pending { width: 100%; aspect-ratio: 16 / 10; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 7px; background: #f5f7f6; color: #66746c; }.history-group-pending > i { color: #4b78ca; font-size: 18px; }.history-group-pending > strong { max-width: calc(100% - 20px); font-size: 10px; }.history-group-pending > span { font-size: 9px; }.history-group-download { width: 44px; height: 44px; position: absolute; z-index: 2; right: 8px; bottom: 8px; display: grid; place-items: center; border: 1px solid rgba(255,255,255,.78); border-radius: 50%; padding: 0; background: rgba(37,99,235,.94); color: #fff; box-shadow: 0 4px 12px rgba(23,57,117,.22); cursor: pointer; }.history-group-download:hover { background: #164fab; }.history-group-download:focus-visible { outline: 3px solid #fff; outline-offset: -5px; }.history-group-info { min-height: 76px; padding-right: 72px; }.history-group-styles { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }.history-group-styles span { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #dce5f3; border-radius: 999px; padding: 5px 8px; background: #f8fbff; color: #425a7c; font-size: 9px; font-weight: 700; }.history-group-styles i { color: #2563eb; }.history-group-delete { width: 44px; height: 44px; bottom: 16px; }.history-group-card:hover { transform: none!important; }
.history-type-divider { grid-column: 1 / -1; min-height: 42px; display: flex; align-items: center; gap: 12px; margin: 2px 0 0; color: #526159; }.history-type-divider > i { min-width: 24px; height: 1px; flex: 1; background: linear-gradient(90deg,rgba(188,199,192,.18),#bdc8c1); }.history-type-divider > i:last-child { background: linear-gradient(90deg,#bdc8c1,rgba(188,199,192,.18)); }.history-type-divider > span { display: inline-flex; align-items: center; gap: 9px; border: 1px solid #d8e0db; border-radius: 999px; padding: 7px 12px; background: #f8faf9; box-shadow: 0 3px 10px rgba(37,51,43,.04); white-space: nowrap; }.history-type-divider b { display: inline-flex; align-items: center; gap: 5px; color: #445149; font-size: 10px; }.history-type-divider b i { color: #63736a; }.history-type-divider small { color: #89938d; font-size: 9px; }
.history-group-download-all { min-width: 128px; min-height: 44px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: 1px solid #2563eb; border-radius: 6px; padding: 0 13px; background: #2563eb; color: #fff; font-size: 10px; font-weight: 800; cursor: pointer; transition: background .16s ease, border-color .16s ease, box-shadow .16s ease; }.history-group-download-all:hover:not(:disabled) { border-color: #164fab; background: #164fab; box-shadow: 0 5px 14px rgba(37,99,235,.2); }.history-group-download-all:focus-visible { outline: 3px solid rgba(37,99,235,.28); outline-offset: 2px; }.history-group-download-all:disabled { cursor: wait; opacity: .58; }
.history-section > header { display:flex; align-items:center; justify-content:space-between; gap:16px; }
.history-refresh { min-height:44px; display:inline-flex; align-items:center; justify-content:center; gap:7px; border:1px solid #cbd8ef; border-radius:6px; padding:0 14px; background:#f7faff; color:#2559b2; font-size:10px; font-weight:700; cursor:pointer; }
.history-refresh:hover:not(:disabled) { border-color:#2563eb; background:#edf4ff; }
.history-refresh:disabled { cursor:wait; opacity:.65; }
.history-empty { min-height:170px; display:flex; align-items:center; justify-content:center; flex-direction:column; border:1px dashed #d7dfd9; border-radius:8px; background:#fafbfa; color:#8a968f; text-align:center; }
.history-empty > i { margin-bottom:9px; color:#6c91df; font-size:25px; }
.history-empty strong { color:#4d5b53; font-size:12px; }
.history-empty span { margin-top:5px; font-size:9px; }
@keyframes spin { to { transform: rotate(360deg); } }@keyframes pulse { 0%,100% { opacity:.3; transform:translateY(0); } 50% { opacity:1; transform:translateY(-3px); } }
.history-created-at { display: flex; align-items: baseline; gap: 7px; }
.history-created-at b { color: #68746c; font-size: 9px; font-weight: 700; }
.history-created-at time { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.history-failure-reason { display:flex; align-items:flex-start; gap:6px; margin-top:2px; border-radius:5px; padding:7px 8px; background:#fff3f2; color:#b43b35 !important; line-height:1.45; }
.history-failure-reason > i { flex:0 0 auto; margin-top:1px; }
.history-failure-reason > span { min-width:0; display:-webkit-box; overflow:hidden; -webkit-box-orient:vertical; -webkit-line-clamp:2; white-space:normal; overflow-wrap:anywhere; }
.history-failure-reason b { margin-right:5px; color:#94332f; font-size:9px; }
.history-failure-reason code { display:inline-block; margin:0 5px 2px 0; border:1px solid #efc5c2; border-radius:3px; padding:1px 4px; background:#fff; color:#9d332d; font-family:ui-monospace,SFMono-Regular,Consolas,monospace; font-size:9px; }
.history-image-dimension { min-height:38px; display:flex; align-items:center; gap:7px; border-top:1px solid #e2e7e3; border-bottom:1px solid #e7ebe8; padding:0 12px; background:#f8faf9; color:#7b8780; font-size:9px; }
.history-image-dimension > i { color:#5076b9; font-size:12px; }
.history-image-dimension > span { white-space:nowrap; }
.history-image-dimension > strong { margin-left:auto; color:#34443b; font-size:10px; font-variant-numeric:tabular-nums; }
.history-group-result > .history-image-dimension { border-top:0; }
.history-info:not(.history-group-info) { padding-right:136px; }
.history-workflow-button { width:34px; height:34px; position:absolute; right:92px; bottom:17px; display:grid; place-items:center; border:1px solid #dce2de; border-radius:5px; padding:0; background:#fff; color:#2563eb; cursor:pointer; }
.history-workflow-button:hover:not(:disabled) { border-color:#9ab5e9; background:#f4f8ff; }
.history-workflow-button:focus-visible { outline:2px solid #2563eb; outline-offset:2px; }
.history-group-workflow-button { width:32px; height:32px; position:absolute; z-index:2; right:44px; bottom:3px; display:grid; place-items:center; border:1px solid rgba(255,255,255,.78); border-radius:50%; padding:0; background:rgba(37,99,235,.94); color:#fff; box-shadow:0 3px 8px rgba(23,57,117,.18); cursor:pointer; }
.history-group-workflow-button:hover:not(:disabled) { background:#164fab; }
.history-group-workflow-button:focus-visible { outline:3px solid #fff; outline-offset:-5px; }
.history-group-source { min-width:0; background:#eef1ef; }
.history-group-source > .history-thumb { width:100%; }
.history-group-source > .history-image-dimension { border-top:0; }
.history-group-result > .history-group-download { width:32px; height:32px; right:6px; bottom:3px; box-shadow:0 3px 8px rgba(23,57,117,.18); }
.history-group-result > .history-group-download i,
.history-group-result > .history-group-workflow-button i { font-size:12px; }
@media (pointer: coarse) {
  .history-group-result > .history-group-download { width:44px; height:44px; right:8px; bottom:8px; }
  .history-group-result > .history-group-workflow-button { width:44px; height:44px; right:60px; bottom:8px; }
}
@media (max-width: 1180px) { .replace-flow { grid-template-columns: 1fr; }.generation-bar { grid-template-columns: auto 1fr; }.generation-intro { grid-row: 1 / 2; }.generate-button { grid-column: 1 / 3; width: 100%; }.output-settings { justify-content: flex-end; }.effect-dropzone { height: 390px; }.comparison-grid { grid-template-columns: 1fr 1fr; }.source-card { grid-column: 1 / 3; }.history-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 767px) { .prompt-preview-panel > header { align-items: flex-start; flex-direction: column; }.prompt-preview-panel > header button { align-self: flex-end; }.style-replace-workspace { width: calc(100% - 26px); margin-top: 18px; }.replace-hero { flex-direction: column; }.replace-flow { display: block; }.flow-panel + .flow-panel { margin-top: 12px; }.effect-dropzone { height: 260px; }.ratio-preflight-warning { grid-template-columns: auto minmax(0,1fr); }.ratio-preflight-warning button { grid-column: 1 / -1; width: 100%; }.style-grid,.lighting-spec-grid { grid-template-columns: repeat(2,1fr); }.lighting-multi-toolbar { grid-column: 1 / -1; }.simple-lighting-control { min-height: 250px; padding: 24px 18px; }.style-reference-heading { flex-direction: column; gap: 5px; }.style-reference-heading small { justify-content: flex-start; text-align: left; }.style-reference-empty { grid-template-columns: 38px minmax(0,1fr); }.style-reference-empty > b { display: none; }.generation-bar { display: flex; align-items: stretch; flex-direction: column; gap: 14px; }.generation-intro { min-width: 0; border-right: 0; border-bottom: 1px solid #e7ebe8; padding: 0 0 12px; }.output-settings { align-items: flex-start; flex-direction: column; gap: 12px; }.setting-group { width: 100%; justify-content: space-between; }.model-pill { width: 100%; border-left: 0; border-top: 1px solid #e7ebe8; padding: 12px 0 0; }.generate-button { width: 100%; }.generation-error { align-items: flex-start; flex-wrap: wrap; }.generation-error > div { flex-basis: calc(100% - 32px); }.generation-error button { margin-left: 30px; }.comparison-grid,.lighting-result-section .comparison-grid { grid-template-columns: 1fr; }.source-card { grid-column: auto; }.image-stage { height: 260px; }.history-group-images { grid-template-columns: repeat(2,minmax(0,1fr))!important; }.history-group-header { align-items: flex-start; flex-wrap: wrap; }.history-group-header > small { width: 100%; margin-left: 0; }.history-group-download-all { width: 100%; }.history-group-info { padding-right: 58px; } }
@media (max-width: 767px), (pointer: coarse) { .zone-prompt-actions button { min-height: 44px; padding-right: 14px; padding-left: 14px; font-size: 11px; } }
@media (max-width: 520px) { .history-grid { grid-template-columns: 1fr; } }
.history-loading { min-height: 170px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; border: 1px dashed #d6ddd8; border-radius: 7px; background: #fafbfa; color: #7f8a83; text-align: center; }
.history-loading i { color: #4778d6; font-size: 28px; }
.history-loading strong { color: #536058; font-size: 11px; }
.history-loading span { font-size: 9px; }
</style>
